<template>
  <div ref="cameraPreview" class="camera-preview" :class="{ 'is-capturing': showCapturingUi && selectedOrder }" @click="handlePreviewClick">
    <video v-show="cameraReady" ref="previewVideo" class="preview-video" data-testid="camera-preview" autoplay playsinline muted disablePictureInPicture disableRemotePlayback></video>
    <!-- Demo mode: a fixed fictitious photo stands in for the live camera feed. -->
    <img v-if="demoFeedUrl" :src="demoFeedUrl" class="preview-video" alt="" />
    <!-- Two states, never both: the camera is coming up (spinner + one line), or it failed and we
         say which failure it is. The movement carries the meaning — if something spins the app is
         still working on it; a static icon means it has stopped and is waiting for a person.
         In demo mode neither applies: the fixed photo above IS the feed, so the block stays out. -->
    <div v-if="!cameraReady && !demoFeedUrl" class="camera-message" role="status" aria-live="polite">
      <template v-if="cameraFault">
        <BzIcon v-if="systemLane" :name="faultGlyph(cameraFault.icon)" :class="['camera-fault-icon', `tone-${cameraFault.tone}`]" />
        <i v-else :class="['camera-fault-icon', cameraFault.icon, `tone-${cameraFault.tone}`]" aria-hidden="true"></i>
        <h2 class="camera-fault-title" data-testid="camera-fault-title">{{ $t(cameraFault.title) }}</h2>
        <p class="camera-fault-body" data-testid="camera-fault-body">{{ $t(cameraFault.body) }}</p>
        <button class="init-camera-button" data-testid="camera-init" v-if="showInitButton" @click="restartCamera">{{ $t('message.startCamera') }}</button>
        <!-- Untranslated on purpose: this is the one line support reads off a photo of the tablet
             screen, so it must be identical whatever language the restaurant runs in. -->
        <p v-if="cameraErrorCode" class="camera-error-code" data-testid="camera-error-code" translate="no">{{ cameraErrorCode }}</p>
      </template>
      <template v-else>
        <i class="pi pi-spin pi-spinner camera-spinner" data-testid="camera-spinner" aria-hidden="true"></i>
        <p class="camera-fault-body" data-testid="camera-fault-body">{{ cameraMessage }}</p>
        <button class="init-camera-button" data-testid="camera-init" v-if="showInitButton" @click="restartCamera">{{ $t('message.startCamera') }}</button>
      </template>
    </div>
    <transition name="capture-fade">
      <div v-if="showCapturingUi && selectedOrder" class="capturing-overlay">
        {{ captureStatusMessage || $t('message.capturing') }}
      </div>
    </transition>
    <div ref="flash" class="flash"></div>
    <!-- We'll use Web Audio API instead of audio element -->
  </div>
</template>

<script>
import {v4 as uuidv4} from 'uuid';
import Logger from '@/logger';
import { reportDeviceEvent } from '@/deviceEvents';
import { reportForensicEvent, newInteractionId, notePhotoCaptured } from '@/forensicEvents';
import { loafWindow } from '@/perfTelemetry';
import { createPhotoEncoder, encodeCanvasWithCap } from '@/photoEncoder';
import BzIcon from '../ui/components/BzIcon.vue';
import { systemLane } from '../ui/lane';
const logger = new Logger({screen: 'CameraPreviewComponent'});

// Tuned defaults. The stream width is fixed; the photo width (downscale cap) is user-configurable via
// `settings.resolution` because it directly trades off upload size / encode latency against sharpness.
const JPEG_QUALITY = 0.85;          // Balance between legibility (ticket codes) and upload size on poor restaurant networks.
const IDEAL_STREAM_WIDTH = 2560;    // Asked to getUserMedia. Higher values trigger portrait rotation on Samsung drivers.
const DEFAULT_PHOTO_WIDTH = 1920;   // Fallback when settings.resolution is missing or invalid. 1920 keeps ticket codes readable while halving JPEG encode time vs 2560 on mid-range SoCs.
const ALLOWED_PHOTO_WIDTHS = [1600, 1920, 2560]; // Whitelist to avoid arbitrary values from old/broken settings.
const FRAME_RATE_IDEAL = 24;
const FRAME_RATE_MAX = 30;

// Black-image detection. Some Samsung drivers (notably Galaxy A16) and other low-end Android devices
// occasionally return a fully black frame on first capture after the camera initializes or after
// rapid resume from background. Without detection, those photos get uploaded and waste analysis,
// the employee's time, and the customer's order.
const BLACK_DETECT_SAMPLE_SIZE = 32;        // 32x32 = 1024 pixels. Downsampling collapses the full-res frame in one drawImage; iterating 1024 RGBA cells is ~5 ms on Galaxy A16-class SoCs.
const BLACK_DETECT_RGB_SUM_THRESHOLD = 30;  // R+G+B ≤ 30. Real black frames carry tiny non-zero JPEG noise (channel values 0–8); a real ticket photo, even a dim one, has thousands of pixels well above this.
const BLACK_DETECT_RATIO_THRESHOLD = 0.98;  // 98%+ of sampled pixels under threshold ⇒ image is black. Below 98% means actual content is present somewhere in frame.
const BLACK_DETECT_MAX_RETRIES = 2;         // 2 retries after the first failed capture (3 attempts total). More retries makes a genuinely broken camera look frozen.
const BLACK_DETECT_RETRY_DELAY_MS = 150;    // Brief pause between retries so the sensor can re-expose; enough to recover from a transient HAL hiccup, short enough that the user does not perceive it as a hang.
// Crash guard around ImageCapture.takePhoto(). On some Qualcomm HALs (measured on the Honor Pad
// X9A / SM6225 `bengal`) takePhoto pushes the camera HAL into recovery — `CHIUSECASE
// consecutiveRecoveryCount` + `TeardownOverrideSession` in logcat — and Chrome kills the renderer
// with SIGKILL. The page dies mid-call, so no try/catch, no promise rejection and no telemetry ever
// runs: to the operator the app simply restarts. The only thing that survives a SIGKILL is what was
// already written synchronously, hence localStorage (not IndexedDB, whose write would not land in
// time) — the same reasoning ackQueue.js and eventLedger.js already use.
//
// The verdict is a LADDER, not a switch, because the two failure shapes are different. What breaks
// this device is takePhoto() with NO photoSettings on a fresh camera session; the same device
// answers correctly as soon as any call names a geometry (measured: implicit fails, explicit works,
// and every implicit call after that one works too). So a crash on the implicit call demotes to
// 'explicit' — which keeps the mode, and its extra sensor surface, alive — and only a crash on the
// explicit call gives up on takePhoto entirely.
const IMAGECAPTURE_INFLIGHT_KEY = 'bronze_imagecapture_inflight';
const IMAGECAPTURE_VERDICT_KEY = 'bronze_imagecapture_verdict';
const VERDICT_EXPLICIT = 'explicit';  // never call takePhoto() without photoSettings on this device
const VERDICT_BLOCKED = 'blocked';    // never call takePhoto() at all on this device
// A mark written by THIS JS context proves the renderer is alive: whatever removed the component
// (a settings screen, a re-mount, a still-pending promise) was not a SIGKILL. Module scope, so it
// dies exactly when the renderer does — which is the whole point.
let inflightMarkFromThisContext = null;
// Hard ceiling on the encoded JPEG. The CV pipeline forwards the photo to Vertex AI online
// prediction, whose request cap is 1.5 MB — and the payload is base64, which inflates bytes by 4/3.
// So anything above ~1.12 MB is rejected by Vertex with FAILED_PRECONDITION *deterministically*: the
// Step Function's 3 retries all fail, the order never gets an analysis, and nothing surfaces to the
// operator. Capping here (at the only place the bytes are produced) is what keeps that from being
// possible at all. 1.10 MB leaves headroom for the JSON envelope around the image.
const MAX_PHOTO_BYTES = 1100000;
// Re-encode ladder used only when a shot lands over the ceiling — a rare, scene-dependent event
// (a visually busy tray at 1920px). Dropping quality is preferred over downscaling: ticket codes stay
// at full resolution, which is what the operator and the OCR both need.
const RECOMPRESS_QUALITY_STEPS = [0.7, 0.55, 0.4];
// How stale a capture may be before the main-thread fallback refuses to re-take it. The worker's
// deadline is 10s, and 10 seconds after the shutter the bag is gone from the pass and the operator
// may already be building another order — re-encoding the live preview then would attach a photo of
// a different scene to THIS order, back-dated to this capture's timestamp (the S3 key is built from
// it, so nothing downstream could tell it apart). Losing a photo is recoverable; a photo that lies
// about what it shows is the "second photo of an empty table" incident. 1.5s covers a worker that
// answers late, not one that answered a different moment.
const REFREEZE_MAX_AGE_MS = 1500;
// How long the shutter stays locked AFTER a photo lands. This is the anti-duplicate window: two
// photos of the same bag inside it are the same photo twice, which costs an analysis and confuses
// the count. It is measured from the photo, not from the tap, because that is when the window
// actually opens.
const COOLDOWN_AFTER_PHOTO_MS = 2500;
// A capture shorter than this shows no "working on it" UI at all. Around 100ms is the classic
// threshold below which an action reads as instantaneous, so anything that appears and vanishes
// inside it is a flicker, not feedback. Canvas captures (~24ms) never cross it; imageCapture
// (~1.35s of sensor) always does.
const CAPTURING_UI_DELAY_MS = 120;
const CAPTURE_SLOW_THRESHOLD_MS = 900;      // Emit a THROTTLED telemetry warning only when tap->blob exceeds this. Post-fix a capture is ~130-400ms; >900ms means main-thread contention or thermal throttling on that fleet device. Gating on "slow" keeps event volume tiny (PostHog <1M/mo budget).

// Hard deadline on getUserMedia. On devices with a single camera behind a legacy HAL (measured on
// the Sunmi V3_PLUS running the app as an installed PWA) the promise can stay *permanently
// unsettled* when another browsing context still holds the capture session: Chrome connects to the
// device and disconnects within the same second, and neither resolves nor rejects. Every await
// below then hangs forever, `isInitializing` stays true, and the whole recovery machinery
// (constraint fallbacks, retries, the manual start button, the camera_error event) is skipped —
// the preview sits on "Initializing camera..." until the page is reloaded by hand. 6s is well
// clear of a healthy start (285ms measured on that same device) and of a cold sensor on our
// slowest fleet tablets.
const GUM_TIMEOUT_MS = 6000;

// Same-origin instance probe. The installed PWA and a Chrome tab running the app are separate
// browsing contexts that both reach the same BroadcastChannel, so a ping that comes back proves a
// second instance is alive — which on a single-camera device is what holds the camera hostage.
// Used only to turn an opaque timeout into an instruction the operator can act on.
const INSTANCE_CHANNEL_NAME = 'bronze-tracker-instances';
const INSTANCE_PROBE_MS = 400;              // Generous for an in-process postMessage round trip; only ever paid on the failure path.

// While the browser's permission prompt is on screen, getUserMedia stays pending — it is waiting on
// a *person*, not on the driver. GUM_TIMEOUT_MS is sized for a wedged HAL and expires long before
// anyone can read a dialog, which made an ungranted permission surface as TimeoutError and get
// reported as "could not start the camera automatically" with no mention of the permission. When the
// permission is still undecided we wait longer — but only once, and never in silence: the panel says
// what it is waiting for the whole time (see cameraFaultPermissionPendingBody). 20s is long enough
// to tap a dialog that is already on screen and short enough that a *dismissed* prompt, where no
// dialog is left to tap, does not read as the app being frozen.
const GUM_PERMISSION_TIMEOUT_MS = 20000;

// The permission query is advisory: it only makes an error message better. Some Android WebViews
// never settle it, and awaiting it on the init path would hang the whole camera behind a diagnostic
// — the exact failure mode GUM_TIMEOUT_MS exists to prevent, reintroduced one call earlier.
const PERMISSION_QUERY_TIMEOUT_MS = 800;

// Backstop for the entire initCamera() run. Every await inside it is individually bounded, but
// "individually bounded" is a claim that has now been wrong twice, and the cost of being wrong is
// the worst failure this component has: a spinner that never resolves, no retry, no event, and an
// operator who cannot tell a busy camera from a dead app. Sized well above a slow cold start plus
// the permission wait.
const INIT_WATCHDOG_MS = 45000;

// What the failure panel shows, per diagnosed cause. Kept as data so the template stays declarative
// and every cause is forced to carry an instruction, not just a label.
const CAMERA_FAULTS = {
  permissionBlocked: { icon: 'pi pi-lock',                  tone: 'warn',   title: 'message.cameraFaultPermissionTitle',   body: 'message.cameraFaultPermissionBlockedBody' },
  permissionPending: { icon: 'pi pi-lock-open',             tone: 'warn',   title: 'message.cameraFaultPermissionTitle',   body: 'message.cameraFaultPermissionPendingBody' },
  busyOtherApp:      { icon: 'pi pi-exclamation-triangle',  tone: 'warn',   title: 'message.cameraFaultBusyAppTitle',      body: 'message.cameraFaultBusyAppBody' },
  otherWindow:       { icon: 'pi pi-clone',                 tone: 'warn',   title: 'message.cameraFaultOtherWindowTitle',  body: 'message.cameraFaultOtherWindowBody' },
  needsTap:          { icon: 'pi pi-hand-point-up',         tone: 'warn',   title: 'message.cameraFaultNeedsTapTitle',     body: 'message.cameraFaultNeedsTapBody' },
  noCamera:          { icon: 'pi pi-ban',                   tone: 'danger', title: 'message.cameraFaultNoCameraTitle',     body: 'message.cameraFaultNoCameraBody' },
  unknown:           { icon: 'pi pi-exclamation-triangle',  tone: 'danger', title: 'message.cameraFaultUnknownTitle',      body: 'message.cameraFaultUnknownBody' },
};

export const PHOTO_WIDTH_OPTIONS = ALLOWED_PHOTO_WIDTHS;
export const DEFAULT_CAMERA_RESOLUTION = DEFAULT_PHOTO_WIDTH;

/** The camera faults' PrimeIcons as the design system's Material Symbols (the lane's flag on). */
const FAULT_GLYPHS = {
  'pi pi-lock': 'lock',
  'pi pi-lock-open': 'lock_open',
  'pi pi-exclamation-triangle': 'warning',
  'pi pi-clone': 'content_copy',
  'pi pi-hand-point-up': 'touch_app',
  'pi pi-ban': 'block',
};

export default {
  name: 'CameraPreviewComponent',
  components: { BzIcon },
  props: {
    settings: {
      type: Object,
      required: true
    },
    selectedOrder: {
      type: Object,
      default: null
    },
    selectedCameraId: {
      type: String,
      default: null
    },
    immersiveMode: {
      type: Boolean,
      default: false
    },
    // RBE pilot (mobile): show the FULL camera frame (object-fit: contain) instead of a cover-crop, and
    // capture that same full frame. With canvas capture, what the operator sees IS what gets saved, so
    // we must not hide any part of the frame behind a crop.
    fullFrame: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // The service screen this page was painted with (src/ui/lane.js): fixed for the page's life.
      systemLane: systemLane(),
      mediaStream: null,
      videoTrack: null,
      videoElement: null,
      captureCanvas: null,
      captureContext: null,
      streamWidth: 0,
      streamHeight: 0,
      cameraReady: false,
      demoFeedUrl: null,
      cameraMessage: this.$t('message.initializingCamera'),
      showInitButton: false,
      imageCapture: null,
      // Verdict ladder for this device, latched in localStorage so it survives the very restart
      // that revealed it. needsExplicitSize: skip the implicit takePhoto (it either returns garbage
      // or kills us). blocked: skip takePhoto altogether.
      imageCaptureNeedsExplicitSize: false,
      imageCaptureBlocked: false,
      deviceCapabilities: null,
      initAttempts: 0,
      maxInitAttempts: 3,
      initializationQueued: false,
      playbackMonitorInterval: null,
      isDesktopDevice: false,
      audioContext: null,   // Web Audio API context
      soundBuffer: null,    // Decoded AudioBuffer ready to play
      soundArrayBuffer: null, // Raw preloaded MP3 bytes (before decoding)
      soundInitialized: false, // Track if sound is initialized
      soundLoading: false,  // Track if sound is currently loading
      soundUrl: '/camera_sound.mp3', // URL to the camera sound file
      flashTimeout: null,    // Reference to flash effect timeout
      isInitializing: false, // Track if initialization is currently in progress
      captureCooldown: false, // Shutter locked (from the tap until the window closes)
      cooldownRunning: false, // The anti-duplicate window is counting down (from the photo) — drives the ring
      isMobile: false, // Disable tap-to-capture on mobile viewports
      isCapturing: false, // True while takePhoto()/canvas capture is in progress
      // Whether the "working on it" UI (the bright frame and the "Capturing…" overlay) is shown.
      // NOT the same as isCapturing: see the watcher. A canvas capture freezes in ~24ms, and
      // showing then hiding a frame inside 24ms is a flicker nobody can read — worse, the
      // overlay's 300ms leave transition then played AFTER the photo, over the flash.
      showCapturingUi: false,
      captureStatusMessage: '', // Overrides the "Capturing..." overlay text during retries
      instanceChannel: null,    // BroadcastChannel used to detect the app running in a second context
      instanceId: uuidv4(),     // Identifies us on that channel so we ignore our own pong
      cameraFault: null,        // One of CAMERA_FAULTS once a failure is diagnosed; null while starting up
      cameraFaultKey: null,     // la CLAVE del fallo ('busyOtherApp', 'permissionBlocked'...) para telemetría/soporte
      cameraErrorCode: '',      // "<ErrorName> · <stage>" shown verbatim under the message, for support
      permissionStatus: null,   // PermissionStatus for the camera, when the browser exposes one
      // Último estado de permiso conocido ('granted' | 'denied' | 'prompt' | 'unknown'), para
      // poder informarlo hacia arriba sin volver a consultar. Lo consume el mensaje de soporte:
      // decir "sin permiso" con la cámara funcionando manda a soporte a mirar donde no falla.
      lastPermission: 'unknown',
      initRetryTimer: null,     // Pending initCamera() retry, so it can be cancelled on unmount
      initWatchdog: null,       // Backstop timer: turns any unforeseen init hang into a visible fault
      isUnmounted: false        // Set in beforeUnmount; every async continuation checks it before touching torn-down state
    };
  },

  computed: {
    imageQuality() {
      return JPEG_QUALITY;
    },
    maxPhotoWidth() {
      const raw = this.settings?.resolution;
      return ALLOWED_PHOTO_WIDTHS.includes(raw) ? raw : DEFAULT_PHOTO_WIDTH;
    }
  },

  async mounted() {
    logger.console('[CAMERA] CameraPreviewComponent mounted');

    // Before anything else: did the previous run die inside takePhoto()?
    this._resolveImageCaptureGuard();

    this.isMobile = window.matchMedia('(max-width: 767px)').matches;

    this.debouncedHandleResize = this.debounce(this.handleResize, 250);
    window.addEventListener('resize', this.debouncedHandleResize);

    // Detect screen lock/unlock (Android camera freeze issue)
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // An orderly departure (tab close, reload — including the remote `refresh` action from Hub —
    // or navigation) must not leave a mark behind: only a kill should. pagehide fires for all of
    // the former and for none of the latter, so clearing here is what keeps the guard honest.
    window.addEventListener('pagehide', this._clearInflightMark);

    // Answer other instances' probes. Opened unconditionally (not only when we hit an error) so
    // that whichever instance fails to get the camera can find the one that already has it.
    if (typeof BroadcastChannel !== 'undefined') {
      this.instanceChannel = new BroadcastChannel(INSTANCE_CHANNEL_NAME);
      this.instanceChannel.addEventListener('message', (event) => {
        if (event.data?.type === 'instance-ping' && event.data.id !== this.instanceId) {
          // A closed channel throws InvalidStateError on postMessage. This is a diagnostic
          // side-channel: it must never surface as an app error.
          try {
            this.instanceChannel?.postMessage({ type: 'instance-pong', id: this.instanceId });
          } catch (error) {
            logger.console('Could not answer instance probe', error);
          }
        }
      });
    }

    // Watch the camera permission so that granting it from the browser UI starts the camera without
    // the operator having to find our button afterwards — the moment they tap "Allow" is exactly the
    // moment they expect the camera to appear.
    this.watchCameraPermission();

    // Add orientation change listener for all devices
    window.addEventListener('orientationchange', () => {
      logger.console('Orientation changed, updating layout');
      setTimeout(() => {
        this.handleResize();
      }, 300);
    });

    // The shutter's audio is created and its sound preloaded below, at mount, but it can only be
    // started (and restarted) inside a real gesture: see unlockAudio. On iOS the shutter plays through
    // an <audio> element instead (playShutterElement).
    this._onAudioGesture = () => this.unlockAudio();
    document.addEventListener('touchend', this._onAudioGesture, { capture: true, passive: true });
    document.addEventListener('click', this._onAudioGesture, { capture: true, passive: true });
    if (this.isIOS()) this.shutterElement();

    // Capture-phase probe for the REAL moment the finger landed. Two reasons this cannot be the
    // click handler: (1) `click` fires on touchEND, so it already excludes how long the finger was
    // down plus any delay a busy main thread added before dispatching — exactly the component that
    // grows when the app is struggling; (2) the shutter is `:disabled` during the 2.5s cooldown,
    // and a disabled control fires no pointer or mouse events at all, so the operator's second tap
    // (the "it goes faster if I take two photos" workaround) produces NO telemetry today. Touch
    // events ARE still delivered over a disabled control, which is what makes it visible here.
    document.addEventListener('touchstart', this._onTapProbe, true);

    // Also try to initialize eagerly (will work on desktop, may be suspended on mobile)
    this.initSound();
    this.preloadSoundBuffer();

    // Wait a brief moment for settings to propagate from parent
    // Parent's loadSettings should complete before this component mounts,
    // but Vue's reactivity system may need a tick to update props
    await this.$nextTick();
    if (__BRONZE_DEMO__) {
      // Demo mode: never touch the real camera. Show a fixed fictitious photo as the feed.
      // ?photo=N picks a different fake-client photo so two features of the same app
      // don't reuse the same screenshot (see CONTENT_STYLE.md).
      const { demoPhotoUrls } = await import('@/demo/photos');
      let photoSeed = 0;
      try {
        photoSeed = parseInt(new URLSearchParams(window.location.search).get('photo') || '0', 10) || 0;
      } catch (e) {
        /* ignore */
      }
      this.demoFeedUrl = demoPhotoUrls(1, photoSeed)[0];
      return;
    }
    this.initCamera();
  },

  watch: {
    selectedCameraId(newId, oldId) {
      if (this.cameraReady && !this.isInitializing && newId !== oldId) {
        logger.console(`User changed camera from ${oldId} to ${newId}, reinitializing`);
        this.stopCamera();
        this.initCamera();
      }
    },
    'settings.cameraExposureComp'() {
      if (this.videoTrack) {
        const caps = this.videoTrack.getCapabilities?.();
        this.applyExposureCompensation(caps);
      }
    },
    immersiveMode() {
      this.handleResize();
      this.applyVideoTransforms();
    },
    /**
     * Gates the capture UI behind a delay, in one place rather than at the eight sites that clear
     * isCapturing. Under the threshold the operator perceives the capture as instant and nothing
     * should appear at all (canvas: ~24ms). Over it — imageCapture waits ~1.35s on the sensor —
     * the frame and the overlay are the only thing telling them the tablet is working.
     */
    isCapturing(active) {
      clearTimeout(this._capturingUiTimer);
      if (!active) {
        this.showCapturingUi = false;
        return;
      }
      this._capturingUiTimer = setTimeout(() => {
        if (this.isCapturing) this.showCapturingUi = true;
      }, CAPTURING_UI_DELAY_MS);
    },
    selectedOrder(newVal) {
      if (!newVal && this.isCapturing) {
        logger.warning('selectedOrder cleared while capture in progress, resetting isCapturing');
        this.isCapturing = false;
        this.$emit('capture-in-progress', false);
      }
      // Recalculate after DOM updates (bottom bar changes)
      this.$nextTick(() => this.handleResize());
    }
  },
  
  beforeUnmount() {
    this.stopCamera();
    window.removeEventListener('resize', this.debouncedHandleResize);
    window.removeEventListener('orientationchange', this.handleResize);
    this.stopPlaybackMonitoring();
    document.removeEventListener('touchstart', this.initSound);
    document.removeEventListener('touchstart', this._onTapProbe, true);
    document.removeEventListener('click', this.initSound);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pagehide', this._clearInflightMark);
    this.isUnmounted = true;
    // A retry scheduled before unmount would otherwise run against a torn-down component: the
    // channel is already closed, so the probe throws and the real camera error is lost.
    clearTimeout(this.initRetryTimer);
    this.initRetryTimer = null;
    clearTimeout(this.initWatchdog);
    this.initWatchdog = null;
    if (this.permissionStatus) {
      this.permissionStatus.onchange = null;
      this.permissionStatus = null;
    }
    this.instanceChannel?.close();
    // Nulling it is what makes hasOtherInstance() short-circuit instead of posting to a closed channel.
    this.instanceChannel = null;

    // The encode worker outlives individual camera restarts (stopCamera/initCamera keeps it, so a
    // camera switch doesn't re-pay startup) but must not outlive the component.
    this._photoEncoder?.terminate();
    this._photoEncoder = null;
    clearTimeout(this._cooldownTimer);
    clearTimeout(this._capturingUiTimer);

    document.removeEventListener('touchend', this._onAudioGesture, { capture: true });
    document.removeEventListener('click', this._onAudioGesture, { capture: true });
    // Clean up audio context if it exists
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(err => logger.console('Error closing audio context:', err));
    }
  },
  
  methods: {
    faultGlyph(pi) {
      return FAULT_GLYPHS[pi] || 'warning';
    },
    handlePreviewClick() {
      if (this.isMobile) return;
      this.capturePhoto();
    },
    /**
     * Records when the finger actually landed, and reports the taps the app silently eats.
     *
     * `event.timeStamp` shares its time origin with performance.now(), so the difference between
     * it and the moment a handler runs IS the input delay — no Event Timing observer needed.
     */
    _onTapProbe(event) {
      try {
        const target = event.target?.closest?.('.camera-button, .preview-video, .camera-preview');
        if (!target) return;
        this._lastTapAt = event.timeStamp;
        if (!this.captureCooldown) return;
        // A tap during the cooldown on the (disabled) shutter never reaches capturePhoto, so it
        // produces neither capture_tap nor capture_cooldown_dropped. That blind spot is precisely
        // where the "take two photos to speed it up" behaviour lives.
        reportForensicEvent('capture_tap_blocked', {
          order_id: this.selectedOrder?.orderId || null,
          ms_since_last: this._fxLastCaptureAt ? Math.round(performance.now() - this._fxLastCaptureAt) : null,
          // Since the SHUTTER, not since the tap: this is what says whether the 2.5s window is
          // longer than operators actually need, which is how its length gets decided with data.
          ms_since_shutter: this._fxLastShutterAt ? Math.round(performance.now() - this._fxLastShutterAt) : null,
          on_shutter: !!event.target?.closest?.('.camera-button'),
        });
      } catch (error) {
        // A telemetry probe must never interfere with a tap.
      }
    },
    /**
     * The tap time to measure from. Prefers the touch probe (the real finger-down) and falls back
     * to now() on desktop or if the probe missed — never to a stale value, which would invent a
     * huge delay on the next capture.
     */
    _tapOrigin() {
      const tap = this._lastTapAt;
      this._lastTapAt = null;
      const now = performance.now();
      return tap != null && now - tap >= 0 && now - tap < 1500 ? tap : now;
    },
    /**
     * Everything about the app's state at the moment of the shutter that could plausibly explain a
     * slow capture. These are the covariables that let a query separate the competing hypotheses —
     * memory, the upload queue running on the main thread, a render cascade with many orders on
     * screen, or a muted camera track — instead of leaving us with "it's slow".
     */
    _captureCovariables() {
      const out = {};
      try {
        const track = this.videoTrack?.getSettings?.();
        if (track) {
          out.stream = `${track.width}x${track.height}@${Math.round(track.frameRate || 0)}`;
        }
        out.track_muted = this.videoTrack ? !!this.videoTrack.muted : null;
        out.device_memory = navigator.deviceMemory || null;
        // True when a previous capture has not emitted yet: the direct test of the "two photos"
        // theory — is the second tap concurrent with the first, or merely after it?
        out.overlapping = !!this._captureInFlight;
      } catch (error) {
        // Best effort.
      }
      return out;
    },
    /**
     * Returns true if running on iOS (iPhone/iPad).
     * On iOS, ImageCapture.takePhoto() returns stream-resolution images only,
     * so we use canvas capture with a high-resolution stream instead.
     */
    isIOS() {
      return /iPhone|iPad|iPod/.test(navigator.userAgent) ||
        (/Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
    },
    /**
     * Waits until the video element has at least one decodable frame.
     * Returns true if ready, false if timed out.
     */
    waitForVideoReady(maxWaitMs = 2000) {
      return new Promise((resolve) => {
        if (this.previewVideo && this.previewVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          resolve(true);
          return;
        }

        const startTime = Date.now();
        const checkInterval = setInterval(() => {
          if (this.previewVideo && this.previewVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            clearInterval(checkInterval);
            resolve(true);
          } else if (Date.now() - startTime > maxWaitMs) {
            clearInterval(checkInterval);
            logger.warning(`Video not ready after ${maxWaitMs}ms, readyState: ${this.previewVideo?.readyState}`);
            resolve(false);
          }
        }, 50);
      });
    },
    // getUserMedia that is guaranteed to settle. See GUM_TIMEOUT_MS for why the raw promise cannot
    // be trusted to do so on its own.
    getUserMediaWithTimeout(constraints, timeoutMs = GUM_TIMEOUT_MS) {
      const request = navigator.mediaDevices.getUserMedia(constraints);
      let timer = null;

      const deadline = new Promise((_, reject) => {
        timer = setTimeout(() => {
          // A stream that lands after we gave up has no owner. Stopping it is what keeps the
          // device free for the retry — otherwise the abandoned track holds the camera and every
          // later attempt times out too.
          request
            .then(stream => stream.getTracks().forEach(track => track.stop()))
            .catch(() => {});

          const error = new Error(`getUserMedia did not settle within ${timeoutMs}ms`);
          error.name = 'TimeoutError';
          reject(error);
        }, timeoutMs);
      });

      // clearTimeout on the winning path is what stops the handler above from stopping a stream we
      // actually went on to use.
      return Promise.race([request, deadline]).finally(() => clearTimeout(timer));
    },
    // Answers "is this app also open somewhere else on this device?" — the one question that turns
    // a camera timeout into something the operator can fix themselves.
    async hasOtherInstance() {
      if (typeof BroadcastChannel === 'undefined' || !this.instanceChannel) {
        return false;
      }

      return new Promise(resolve => {
        let found = false;
        const channel = this.instanceChannel;
        const onMessage = (event) => {
          if (event.data?.type === 'instance-pong' && event.data.id !== this.instanceId) {
            found = true;
          }
        };

        // This probe only exists to make an error message more useful, so it must never *become*
        // the error: a channel closed between the guard above and here (unmount, or the browser
        // reclaiming it) throws InvalidStateError, which would otherwise escape
        // handleCameraUnavailable, hit initCamera's outer catch, and replace the real
        // NotReadableError with a BroadcastChannel error that says nothing about the camera.
        try {
          channel.addEventListener('message', onMessage);
          channel.postMessage({ type: 'instance-ping', id: this.instanceId });
        } catch (error) {
          logger.console('Instance probe unavailable, assuming no other instance', error);
          try {
            channel.removeEventListener('message', onMessage);
          } catch (cleanupError) {
            logger.console('Instance probe cleanup failed', cleanupError);
          }
          resolve(false);
          return;
        }

        setTimeout(() => {
          try {
            channel.removeEventListener('message', onMessage);
          } catch (error) {
            logger.console('Instance probe cleanup failed', error);
          }
          resolve(found);
        }, INSTANCE_PROBE_MS);
      });
    },
    // Explicit "Start camera" tap. Distinct from initCamera() because a person asking again is a
    // fresh attempt series, not the continuation of the automatic one that already gave up.
    restartCamera() {
      clearTimeout(this.initRetryTimer);
      this.initRetryTimer = null;
      clearTimeout(this.initWatchdog);
      this.initWatchdog = null;
      // A hung run left this true and would make initCamera() refuse the tap as a duplicate.
      this.isInitializing = false;
      this.initAttempts = 0;
      this.cameraErrorCode = '';
      this.cameraFault = null;
      this.initCamera();
    },
    // 'granted' | 'denied' | 'prompt' | 'unknown'. Safari and older Android WebViews either lack
    // navigator.permissions or reject the 'camera' name, and iOS never exposes it — 'unknown' is a
    // normal answer there, not an error, and every caller must keep working without it.
    // Estado REAL de la cámara hacia arriba (CameraScreen -> App.vue -> mensaje de soporte).
    // `ready` es la verdad de campo: hay fotogramas decodificables en el vídeo. El permiso es un
    // dato aparte, porque 'granted' con la cámara ocupada por otra app sigue siendo un fallo.
    reportCameraState() {
      this.$emit('camera-state', {
        ready: this.cameraReady,
        permission: this.lastPermission,
        fault: this.cameraFault ? this.cameraFaultKey : null,
        code: this.cameraErrorCode || null,
      });
    },
    async cameraPermissionState() {
      try {
        const query = navigator.permissions?.query({ name: 'camera' });
        if (!query) return 'unknown';
        // Race, never await bare: an unsettled query would hold the camera hostage behind a lookup
        // whose only job is to word an error message better.
        const status = await Promise.race([
          query,
          new Promise(resolve => setTimeout(() => resolve(null), PERMISSION_QUERY_TIMEOUT_MS)),
        ]);
        this.lastPermission = status?.state || 'unknown';
        return this.lastPermission;
      } catch (error) {
        logger.console('Camera permission state unavailable', error);
        return 'unknown';
      }
    },
    async watchCameraPermission() {
      try {
        const status = await navigator.permissions?.query({ name: 'camera' });
        if (!status) return;
        this.permissionStatus = status;
        this.lastPermission = status.state || 'unknown';
        this.reportCameraState();
        status.onchange = () => {
          logger.console(`Camera permission changed to ${status.state}`);
          this.lastPermission = status.state || 'unknown';
          this.reportCameraState();
          // Only 'granted' is worth acting on: 'denied' is already the panel we are showing, and
          // re-initialising on it would spin the retry ladder against a permission that cannot work.
          if (status.state === 'granted' && !this.cameraReady && !this.isUnmounted) {
            this.restartCamera();
          }
        };
      } catch (error) {
        logger.console('Cannot observe camera permission', error);
      }
    },
    // Turns a DOMException name into something the person holding the tablet can act on. The names
    // are what the fleet actually reports; each maps to a different physical fix, and until now they
    // all rendered as "could not start the camera", which is why a blocked permission and a camera
    // held by another app were indistinguishable from the shop floor.
    cameraErrorGuidance(error, permissionState) {
      switch (error?.name) {
        // The camera exists and we are allowed to use it, but something else holds the sensor (another
        // Android app, or a driver that did not release it). By far the most common failure.
        case 'NotReadableError':
        case 'TrackStartError':          // Older Chrome name for the same condition.
          return 'busyOtherApp';
        // The permission was denied — for the site, or OS-wide for the browser. No retry can fix it.
        case 'NotAllowedError':
        case 'PermissionDeniedError':
          return 'permissionBlocked';
        // No videoinput at all: camera disabled in the tablet settings, or genuinely absent.
        case 'NotFoundError':
        case 'DevicesNotFoundError':
          return 'noCamera';
        default:
          // A timeout says only "nothing came back", so the permission state is what gives it a
          // meaning: undecided means the prompt is still waiting for someone to answer it, which is
          // the single most common way a tablet ends up here on a fresh install.
          if (permissionState === 'prompt') return 'permissionPending';
          if (permissionState === 'denied') return 'permissionBlocked';
          return 'unknown';
      }
    },
    showCameraFault(faultKey, { errorName = 'Error', stage = 'unknown', error, ...extra } = {}) {
      this.cameraFault = CAMERA_FAULTS[faultKey] || CAMERA_FAULTS.unknown;
      // The support code is deliberately the raw error name plus the stage: it is what a screenshot
      // from a restaurant has to carry for the failure to be diagnosable without a CloudWatch query,
      // and translating it would defeat that.
      this.cameraErrorCode = `${errorName} · ${stage}`;
      // Lifted to App.vue (via CameraScreen) so a help request can carry the exact fault and
      // support code without anyone having to read them off the screen.
      this.cameraFaultKey = faultKey;
      this.reportCameraState();
      this.showInitButton = true;
      this.isInitializing = false;
      // A diagnosis exists, so the backstop has nothing left to catch.
      clearTimeout(this.initWatchdog);
      this.initWatchdog = null;

      // Anything the operator is told, ops can see. reportDeviceEvent throttles to one event per
      // type per minute, so a retry loop that shows the same panel repeatedly cannot flood the bus.
      reportDeviceEvent('camera_error', {
        stage,
        errorName,
        fault: faultKey,
        error: error !== undefined ? String(error) : `Camera fault: ${faultKey}`,
        ...extra,
      }, 'critical');
    },
    // Every deferred init goes through here so that unmount can cancel it. A retry that survives
    // teardown runs with the camera stopped and the instance channel closed, which turns a real
    // camera failure into a misleading one.
    scheduleInitRetry(delayMs = 1000) {
      clearTimeout(this.initRetryTimer);
      this.initRetryTimer = setTimeout(() => {
        this.initRetryTimer = null;
        if (this.isUnmounted) {
          logger.console('Skipping camera retry: component unmounted');
          return;
        }
        this.initCamera();
      }, delayMs);
    },
    // Terminal path for "the camera exists but we could not get hold of it". Shared by every
    // getUserMedia attempt so that a timeout can never leave the component wedged.
    async handleCameraUnavailable(error, stage) {
      // We have a diagnosis, so the backstop has nothing left to catch.
      clearTimeout(this.initWatchdog);
      this.initWatchdog = null;

      const otherInstance = await this.hasOtherInstance();
      const permissionState = await this.cameraPermissionState();

      // A second instance of our own app is a more specific diagnosis than anything the error name
      // carries, so it wins when we can prove it.
      this.showCameraFault(otherInstance ? 'otherWindow' : this.cameraErrorGuidance(error, permissionState), {
        errorName: error?.name || 'Unknown',
        stage,
        error,
        otherInstance,
        // The permission state is what separates "the driver is wedged" from "nobody answered the
        // prompt" on an otherwise identical TimeoutError.
        permissionState,
      });

      logger.warning(`Camera unavailable at ${stage}`, { error: String(error), otherInstance });

      // A pending or denied permission does not change by asking again — Chrome will not re-show a
      // prompt that is already up or dismissed. Retrying burns the budget and keeps the operator on
      // a spinner; the permission observer restarts us the instant they answer.
      const awaitingHuman = permissionState === 'prompt' || permissionState === 'denied';

      if (!awaitingHuman && this.initAttempts < this.maxInitAttempts) {
        this.initAttempts++;
        logger.console(`Retry attempt ${this.initAttempts}/${this.maxInitAttempts} scheduled`);
        this.scheduleInitRetry();
      }
    },
    buildConstraints(overrideCameraId) {
      const onIOS = this.isIOS();
      const video = {
        width: { ideal: IDEAL_STREAM_WIDTH },
      };

      // Device selection. Callers can pass overrideCameraId (e.g. after validating it against
      // enumerateDevices) instead of using the raw selectedCameraId prop.
      const deviceId = overrideCameraId !== undefined ? overrideCameraId : this.selectedCameraId;
      if (deviceId) {
        video.deviceId = { exact: deviceId };
      } else {
        video.facingMode = { ideal: 'environment' };
      }

      if (onIOS) {
        // iOS needs resizeMode: 'none' to get a stream that matches the requested resolution.
        video.resizeMode = 'none';
      } else {
        video.frameRate = { ideal: FRAME_RATE_IDEAL, max: FRAME_RATE_MAX };
      }

      return { video };
    },
    async initCamera() {
      // Demo mode never touches the real camera — the fixed photo in mounted() is
      // the feed. Guarded here, at the single entry point, because initCamera is
      // reachable from more places than mounted(): the permission watcher, the
      // visibility handler, restartCamera and the manual button.
      if (__BRONZE_DEMO__) return;

      if (this.isUnmounted) {
        logger.console('Camera initialization skipped: component unmounted');
        return;
      }

      // Prevent concurrent initializations
      if (this.isInitializing) {
        logger.console('Camera initialization already in progress, skipping duplicate call');
        return;
      }

      this.isInitializing = true;
      // Arm before the first await. Whatever hangs — a getUserMedia the deadline missed, a
      // permission query, a video element that never fires its events — the operator ends up looking
      // at a named failure with a button, not at a spinner that will never stop. isInitializing is
      // cleared too, or the manual restart would be refused as a duplicate.
      clearTimeout(this.initWatchdog);
      this.initWatchdog = setTimeout(() => {
        this.initWatchdog = null;
        if (this.cameraReady || this.isUnmounted || !this.isInitializing) return;
        logger.error(`Camera init exceeded ${INIT_WATCHDOG_MS}ms without resolving; surfacing a fault`);
        this.showCameraFault('unknown', {
          errorName: 'InitWatchdog',
          stage: 'init_watchdog',
          error: `Camera init did not resolve within ${INIT_WATCHDOG_MS}ms`,
        });
      }, INIT_WATCHDOG_MS);

      try {
        logger.console('=== CAMERA INIT START ===');
        // On a retry, say so: flipping straight back to "Initializing camera..." reads as the app
        // having forgotten the error it showed a second ago, and an operator who cannot tell a retry
        // from a fresh start has no way to know whether waiting will help.
        this.cameraMessage = this.initAttempts > 0
          ? this.$t('message.cameraRetryAttempt', { attempt: this.initAttempts, total: this.maxInitAttempts })
          : this.$t('message.initializingCamera');
        // Back to the spinner state: something is happening again, so no static fault icon.
        this.cameraFault = null;
        // The support code deliberately survives the retries — it is cleared only once the camera
        // actually starts, so a tablet stuck in the retry loop still shows why.
        this.showInitButton = false;

        const onIOS = this.isIOS();

        // Validate selectedCameraId against the devices actually exposed by the browser. Chrome
        // regenerates deviceIds after the user clears site data or restarts the browser, leaving
        // stale IDs in our settings. An `exact` constraint with a non-existent deviceId produces
        // OverconstrainedError on the very first getUserMedia, even before width/frameRate kick in.
        let effectiveCameraId = this.selectedCameraId;
        if (effectiveCameraId) {
          try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const stillExists = devices.some(d => d.kind === 'videoinput' && d.deviceId === effectiveCameraId);
            if (!stillExists) {
              logger.warning(`selectedCameraId ${effectiveCameraId.slice(0, 12)}… is stale (not in enumerateDevices), ignoring for this init and emitting stale-camera-id`);
              this.$emit('stale-camera-id', effectiveCameraId);
              effectiveCameraId = null;
            }
          } catch (enumErr) {
            logger.warning('Could not validate selectedCameraId', enumErr);
          }
        }

        let constraints = this.buildConstraints(effectiveCameraId);
        logger.console(`Step 1: Platform is ${onIOS ? 'iOS' : 'Android/other'}`);

        // A denied permission cannot be recovered by any constraint, so asking anyway only costs the
        // operator three failed attempts before we tell them the one thing they need to do.
        const permissionState = await this.cameraPermissionState();
        if (permissionState === 'denied') {
          logger.warning('Camera permission is denied; not attempting getUserMedia');
          this.showCameraFault('permissionBlocked', {
            errorName: 'NotAllowedError',
            stage: 'permission_denied',
            error: 'Camera permission state is denied',
            permissionState,
          });
          return;
        }

        // Undecided permission means the prompt is up and the delay is a human reading it, so the
        // wedged-HAL deadline would abort a request that was about to succeed. Waiting is only
        // acceptable if the panel says what it is waiting for — a spinner reading "Initializing
        // camera..." for 20s while a dialog sits unanswered is indistinguishable from a hang.
        const gumTimeout = permissionState === 'prompt' ? GUM_PERMISSION_TIMEOUT_MS : GUM_TIMEOUT_MS;
        if (permissionState === 'prompt') {
          this.cameraMessage = this.$t('message.cameraFaultPermissionPendingBody');
        }


        try {
          logger.console('Step 1: Requesting media with constraints:', JSON.stringify(constraints));
          this.mediaStream = await this.getUserMediaWithTimeout(constraints, gumTimeout);
          logger.console('Step 1: Media stream obtained successfully');
        } catch (error) {
          // A timeout means the camera never answered, not that it disliked our constraints.
          // Walking down the fallback ladder would burn 12 more seconds asking the same busy
          // device the same question, so go straight to the retry/report path.
          if (error.name === 'TimeoutError') {
            await this.handleCameraUnavailable(error, 'gum_timeout_step1');
            return;
          }

          // Steps 1 and 2 have fallbacks left, so a failure here is a recoverable
          // condition, not an actionable exception — only the terminal Step 3 failure
          // (no camera at all) is reported as an error.
          logger.warning('Step 1: Failed with constrained environment camera', {error: error});

          // Fall back to environment camera with frame rate and width, but no deviceId. Keeps the
          // resolution high so captures stay readable even if the preferred camera/constraints combo
          // was rejected (seen on Samsung Galaxy A16 after the driver gets into a "dirty" state).
          constraints = {
            video: {
              frameRate: { ideal: FRAME_RATE_IDEAL, max: FRAME_RATE_MAX },
              facingMode: { ideal: 'environment' },
              width: { ideal: IDEAL_STREAM_WIDTH }
            }
          };

          try {
            logger.console('Step 2: Retrying with environment camera:', JSON.stringify(constraints));
            this.mediaStream = await this.getUserMediaWithTimeout(constraints, gumTimeout);
            logger.console('Step 2: Media stream obtained successfully');
          } catch (constraintError) {
            if (constraintError.name === 'TimeoutError') {
              await this.handleCameraUnavailable(constraintError, 'gum_timeout_step2');
              return;
            }

            logger.warning('Step 2: Failed with environment camera', {error: constraintError});

            // Fall back to most basic constraints
            constraints = {
              video: {
                facingMode: { ideal: 'environment' }
              }
            };
            
            try {
              logger.console('Step 3: Retrying with basic constraints:', JSON.stringify(constraints));
              this.mediaStream = await this.getUserMediaWithTimeout(constraints, gumTimeout);
              logger.console('Step 3: Media stream obtained successfully');
            } catch (fallbackError) {
              // All constraint fallbacks exhausted: either the tablet is running without a usable
              // camera, or the device is held by another context. handleCameraUnavailable tells
              // those two apart and retries or reports accordingly.
              logger.error('Step 3: Failed to initialize camera with basic constraints', {error: fallbackError});
              await this.handleCameraUnavailable(fallbackError, 'constraints_exhausted');
              return;
            }
          }
        }

        this.videoTrack = this.mediaStream.getVideoTracks()[0];
        const trackSettings = this.videoTrack.getSettings();
        logger.console('Step 4: Video track obtained with settings:', JSON.stringify(trackSettings, null, 2));

        // Listen for the OS killing the track (e.g. memory pressure, another app claiming camera)
        this.videoTrack.addEventListener('ended', () => {
          logger.console('[CAMERA] Video track ended event fired, reinitializing camera');
          this.stopCamera();
          this.initCamera();
        }, { once: true });

        // Reset zoom to minimum to avoid hardware-level digital zoom on Xiaomi and other OEMs.
        const trackCapabilities = this.videoTrack.getCapabilities?.();
        if (trackCapabilities?.zoom) {
          try {
            await this.videoTrack.applyConstraints({ advanced: [{ zoom: trackCapabilities.zoom.min }] });
            logger.console(`Step 4a: Zoom reset to minimum (${trackCapabilities.zoom.min})`);
          } catch (zoomError) {
            logger.console('Step 4a: Zoom reset not supported on this device:', zoomError);
          }
        }

        // Apply exposure compensation if set.
        await this.applyExposureCompensation(trackCapabilities);

        // Emit stream info for settings panel
        this.emitStreamInfo();

        // Enumerate available cameras now that we have permission (enumerateDevices only returns labels after permission granted)
        this.enumerateCameras();

        // Try to initialize ImageCapture if the API is available
        if ('ImageCapture' in window) {
          try {
            logger.console('Step 5: Initializing ImageCapture API');
            this.imageCapture = new ImageCapture(this.videoTrack);
            
            // Get the photo capabilities
            try {
              const capabilities = await this.imageCapture.getPhotoCapabilities();
              this.deviceCapabilities = capabilities;
              logger.console('Step 5: Photo capabilities obtained:', capabilities);
            } catch (capabilitiesError) {
              logger.warning('Step 5: Unable to get photo capabilities', capabilitiesError);
            }
          } catch (imageCaptureError) {
            logger.console('Step 5: ImageCapture API available but failed to initialize', imageCaptureError);
            this.imageCapture = null;
          }
        } else {
          logger.console('Step 5: ImageCapture API not available in this browser');
        }

        logger.console('Step 6: Setting up video element');
        this.previewVideo = this.$refs.previewVideo;

        if (!this.previewVideo) {
          logger.error('Step 6: Preview video element not found');
          this.showCameraFault('unknown', { errorName: 'NoVideoElement', stage: 'video_ref_missing' });
          this.isInitializing = false; // Reset flag on error
          return;
        }
        
        // Fix for iOS Safari from https://leemartin.dev/hello-webrtc-on-safari-11-e8bcb5335295
        logger.console('Step 6: Setting video attributes for cross-platform compatibility');
        
        // Apply all attributes directly to ensure iOS respects them
        this.previewVideo.setAttribute('autoplay', 'true');
        this.previewVideo.setAttribute('muted', 'true');
        this.previewVideo.setAttribute('playsinline', 'true');
        this.previewVideo.setAttribute('webkit-playsinline', 'true'); // iOS specific
        
        // Explicitly set properties as well
        this.previewVideo.autoplay = true;
        this.previewVideo.muted = true;
        this.previewVideo.playsInline = true;
        
        // Disable controls in multiple ways
        this.previewVideo.controls = false;
        this.previewVideo.setAttribute('controls', 'false');
        this.previewVideo.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
        this.previewVideo.setAttribute('disablePictureInPicture', '');
        this.previewVideo.setAttribute('disableRemotePlayback', '');
        
        // Apply additional styles to prevent controls
        this.previewVideo.style.pointerEvents = 'none';
        this.previewVideo.style.userSelect = 'none';
        this.previewVideo.style.webkitUserSelect = 'none';
        
        // Apply hardware acceleration and prevent flickering
        this.previewVideo.style.transform = 'translateZ(0)';
        this.previewVideo.style.backfaceVisibility = 'hidden';
        this.previewVideo.style.webkitBackfaceVisibility = 'hidden';
        
        logger.console('Step 7: Setting video source to media stream');
        this.previewVideo.srcObject = this.mediaStream;

        // Wait for video metadata to load
        logger.console('Step 8: Waiting for video metadata');
        await new Promise((resolve) => {
          const metadataLoaded = () => {
            this.streamWidth = this.previewVideo.videoWidth;
            this.streamHeight = this.previewVideo.videoHeight;
            logger.console(`Step 8: Metadata loaded - Stream dimensions: ${this.streamWidth}x${this.streamHeight}`);
            
            // Reapply control disabling after metadata loads
            this.previewVideo.controls = false;
            this.previewVideo.style.pointerEvents = 'none';
            
            this.previewVideo.removeEventListener('loadedmetadata', metadataLoaded);
            resolve();
          };
          
          this.previewVideo.addEventListener('loadedmetadata', metadataLoaded);
          
          // iOS PWA cold start can take several seconds before loadedmetadata fires.
          // 1s was too short on iPad 10th gen / iOS 18.5 → triggered timeout, then play() hung.
          setTimeout(() => {
            if (!this.streamWidth || !this.streamHeight) {
              this.streamWidth = this.previewVideo.videoWidth || 1280;
              this.streamHeight = this.previewVideo.videoHeight || 1280;
              logger.console(`Step 8: Metadata timeout - Using dimensions: ${this.streamWidth}x${this.streamHeight}`);
              resolve();
            }
          }, onIOS ? 5000 : 1000);
        });

        try {
          // Try to play the video
          logger.console('Step 9: Attempting to play video');
          const playPromise = this.previewVideo.play();
          if (playPromise !== undefined) {
            // iOS PWA can leave play() pending forever when called before metadata is fully ready.
            // Race against a 3s timeout so we can fall back instead of hanging the whole init.
            await Promise.race([
              playPromise,
              new Promise((_, reject) => setTimeout(() => reject(new Error('play() timeout after 3s — likely iOS PWA hang')), 3000))
            ]);
            logger.console('Step 9: Video playback started successfully');

            // Reapply control disabling after play starts
            this.previewVideo.controls = false;
            this.previewVideo.style.pointerEvents = 'none';
          } else {
            logger.console('Step 9: Play promise was undefined, assuming playback started');
          }
        } catch (playError) {
          logger.error('Step 9: Error playing video:', {error: playError.toString(), name: playError.name, message: playError.message});
          
          // For iOS PWA, try a different approach if play fails
          logger.console('Step 9a: Attempting alternative play approach for iOS');
          try {
            // Try to play with user interaction simulation
            this.previewVideo.setAttribute('autoplay', 'true');
            this.previewVideo.muted = true;
            this.previewVideo.playsInline = true;

            // Force play after a short delay
            await new Promise(resolve => setTimeout(resolve, 100));
            await Promise.race([
              this.previewVideo.play(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('Step 9a play() timeout after 3s')), 3000))
            ]);
            logger.console('Step 9a: Alternative play approach succeeded');
          } catch (altPlayError) {
            logger.error('Step 9a: Alternative play approach failed:', {error: altPlayError.toString()});
            // The stream exists; the browser refused to start playback without a gesture. A stop
            // waiting on a person, so it gets the static panel rather than a spinner that will never
            // resolve on its own.
            this.showCameraFault('needsTap', {
              errorName: 'AutoplayBlocked',
              stage: 'video_play_blocked',
              error: altPlayError,
            });
            return;
          }
        }

        await this.$nextTick();
        
        // Initialize capture canvas
        logger.console('Step 10: Initializing capture canvas');
        this.captureCanvas = document.createElement('canvas');
        // alpha:false — the capture canvas is opaque (JPEG has no alpha); skipping the alpha channel
        // and premultiply makes drawImage + readback slightly cheaper. Do NOT add willReadFrequently:
        // this canvas must stay GPU-backed for drawImage (the 32x32 black-sample canvas is the CPU one).
        this.captureContext = this.captureCanvas.getContext('2d', { alpha: false });
        
        logger.console('Step 11: Handling resize and transforms');
        this.handleResize(); // Force resize calculation
        this.applyVideoTransforms();
        
        // Force a repaint to ensure video displays properly
        logger.console('Step 12: Forcing repaint of video element');
        this.previewVideo.style.display = 'none';
        
        // Add additional iOS PWA fixes
        this.previewVideo.style.transform = 'translateZ(0)'; // Force hardware acceleration
        this.previewVideo.style.backfaceVisibility = 'hidden'; // Prevent flickering
        
        // Force a more aggressive repaint
        setTimeout(async () => {
          this.previewVideo.style.display = 'block';

          // Force video refresh
          if (this.mediaStream) {
            logger.console('Step 12a: Refreshing video source');
            const tracks = this.mediaStream.getVideoTracks();
            if (tracks.length > 0) {
              // Verify video is actually playing
              if (this.previewVideo.paused) {
                logger.warning('Video not playing despite successful play promise');

                // Try to reset srcObject
                this.previewVideo.srcObject = null;
                await new Promise(resolve => setTimeout(resolve, 50));
                this.previewVideo.srcObject = this.mediaStream;
                try {
                  await this.previewVideo.play();
                  logger.console('Step 12b: Video playback restarted successfully');
                } catch (e) {
                  logger.error('Final play attempt failed:', {error: e});
                }
              }
            }
          }

          // Wait until the video actually has a decodable frame before marking ready
          logger.console('Step 13: Waiting for video to have decodable frames...');
          const hasFrames = await this.waitForVideoReady(5000);
          if (hasFrames) {
            logger.console('Step 13: Video has decodable frames, marking camera as ready');
          } else {
            logger.warning('Step 13: Timed out waiting for decodable frames, marking camera ready anyway');
          }
          this.cameraReady = true;
          this.cameraErrorCode = '';
          this.cameraFault = null;
          this.cameraFaultKey = null;
          this.reportCameraState(); // la cámara está en marcha: hacia arriba va `ready: true`

          // Pre-warm the capture path (best-effort, fire-and-forget): pay the first-use JPEG encoder
          // allocation + JIT now, on cameraReady, so the operator's FIRST real shot after opening the
          // camera doesn't pay it. The first capture is the one users most notice as "slow to warm up".
          this._prewarmCapturePath();

          // Second resize after a short delay to ensure proper display
          setTimeout(() => {
            this.handleResize();
            this.applyVideoTransforms();
            logger.console('Step 14: Final resize completed');
            logger.console('=== CAMERA INIT COMPLETE ===');

            // Start monitoring video playback
            this.startPlaybackMonitoring();
          }, 200);
        }, 100);

        this.initAttempts = 0; // Reset attempts counter on success
        this.isInitializing = false; // Mark initialization as complete
        clearTimeout(this.initWatchdog);
        this.initWatchdog = null;
        logger.console('Camera initialization complete');
      } catch (error) {
        this.isInitializing = false; // Reset flag on error
        clearTimeout(this.initWatchdog);
        this.initWatchdog = null;
        logger.error('Unhandled error in camera initialization:', {error: error});
        const permissionState = await this.cameraPermissionState();
        this.showCameraFault(this.cameraErrorGuidance(error, permissionState), {
          errorName: error?.name || 'Unknown',
          stage: 'init',
          error,
          permissionState,
        });

        // showCameraFault already reported this one; retrying is about recovery, not visibility.
        if (this.initAttempts < this.maxInitAttempts) {
          this.initAttempts++;
          logger.console(`Retry attempt ${this.initAttempts}/${this.maxInitAttempts} scheduled after error`);
          this.scheduleInitRetry();
        }
      }
    },
    async enumerateCameras() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices
          .filter(d => d.kind === 'videoinput')
          .map(d => ({ deviceId: d.deviceId, label: d.label || `Camera ${d.deviceId.slice(0, 6)}` }));
        logger.console('Cameras enumerated:', cameras.map(c => c.label));
        this.$emit('cameras-enumerated', cameras);
      } catch (err) {
        logger.console('Could not enumerate cameras:', err);
      }
    },
    applyVideoTransforms() {
      if (this.previewVideo) {
        // Apply any necessary transformations
        const transform = [];
        
        if (this.settings.flipHorizontally) {
          transform.push('scaleX(-1)');
        }
        if (this.settings.flipVertically) {
          transform.push('scaleY(-1)');
        }
        
        // Add hardware acceleration to the transform array
        transform.push('translateZ(0)');
        
        this.previewVideo.style.transform = transform.join(' ');
        this.previewVideo.style.backfaceVisibility = 'hidden';
      }
    },
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },
    calculatePreviewHeight() {
      const cameraPreview = this.$refs.cameraPreview;
      if (!cameraPreview) return;
      const isTablet = window.innerWidth >= 768;

      if (this.immersiveMode) {
        this.previewHeight = window.innerHeight;
      } else if (isTablet) {
        // Tablet: camera fits between header and bottom bar
        const header = document.querySelector('.header');
        const bottomBar = document.querySelector('.classic-layout')
          || document.querySelector('.main-container')
          || document.querySelector('.order-info-wrapper');
        const headerHeight = header ? header.offsetHeight : 64;
        const bottomHeight = bottomBar ? bottomBar.offsetHeight : 168;
        this.previewHeight = window.innerHeight - headerHeight - bottomHeight;
      } else if (this.fullFrame) {
        // RBE pilot (mobile): flexbox sizes the preview between the top code bar and the bottom
        // controls. Forcing a pixel height here would fight the flex layout, so let CSS own it.
        cameraPreview.style.height = '';
        return;
      } else {
        // Mobile: video fills the area above the capture footer (same as capture mode)
        this.previewHeight = window.innerHeight - 232;
      }

      cameraPreview.style.height = `${this.previewHeight}px`;
    },
    handleResize() {
      this.calculatePreviewHeight();
      if (!this.streamWidth || !this.streamHeight) {
        logger.console('Resize skipped: missing stream dimensions');
        return;
      }

      const containerElement = this.$refs.cameraPreview;
      if (!containerElement) {
        logger.error('Camera preview container not found');
        return;
      }
      
      if (this.previewVideo) {
        if (this.immersiveMode) {
          // Immersive mode: fill 100% height, width adjusts to aspect ratio (no crop)
          this.previewVideo.style.width = 'auto';
          this.previewVideo.style.height = '100%';
          this.previewVideo.style.maxWidth = 'none';
          this.previewVideo.style.maxHeight = '100%';
          this.previewVideo.style.objectFit = 'contain';
        } else if (window.innerWidth < 768) {
          if (this.fullFrame) {
            // RBE pilot: show the FULL camera frame (contain, no crop). With canvas capture, what the
            // operator sees in the preview is exactly what gets saved, so we must not crop any of it.
            this.previewVideo.style.width = '100%';
            this.previewVideo.style.height = '100%';
            this.previewVideo.style.maxWidth = '100%';
            this.previewVideo.style.maxHeight = '100%';
            this.previewVideo.style.objectFit = 'contain';
          } else {
            // Mobile: fill entire area behind orders overlay
            this.previewVideo.style.width = '100%';
            this.previewVideo.style.height = '100%';
            this.previewVideo.style.maxWidth = 'none';
            this.previewVideo.style.maxHeight = 'none';
            this.previewVideo.style.objectFit = 'cover';
          }
        } else {
          // Tablet normal mode: fill container height, maintain aspect ratio
          this.previewVideo.style.width = 'auto';
          this.previewVideo.style.height = '100%';
          this.previewVideo.style.maxWidth = '100%';
          this.previewVideo.style.maxHeight = '100%';
          this.previewVideo.style.objectFit = 'contain';
        }

        // Center the video
        this.previewVideo.style.display = 'block';
        this.previewVideo.style.margin = '0 auto';
      }
    },
    async capturePhoto() {
      if (!this.selectedOrder || !this.previewVideo || !this.cameraReady) return;
      // Still inside the tap: the only moment iOS lets the audio start (the sound comes later).
      this.unlockAudio();

      // Forensic: record the tap intent BEFORE the cooldown guard, so a tap the
      // cooldown swallows (no photo, no error — the A864E blind spot) is still
      // visible. Each tap gets its own interaction id; a successful one threads
      // that id through to fx_capture_saved / upload, a dropped one is an orphan.
      const fxInteractionId = newInteractionId();
      const fxOrderId = this.selectedOrder.orderId;
      reportForensicEvent('capture_tap', { interaction_id: fxInteractionId, order_id: fxOrderId });
      if (this.captureCooldown) {
        reportForensicEvent('capture_cooldown_dropped', {
          interaction_id: fxInteractionId,
          order_id: fxOrderId,
          ms_since_last: this._fxLastCaptureAt ? Math.round(performance.now() - this._fxLastCaptureAt) : null,
        });
        return;
      }
      this._fxInteractionId = fxInteractionId;
      this._fxLastCaptureAt = performance.now();

      // tapAt is when the FINGER landed (touch probe), tCaptureStart is when this handler got to
      // run. The gap between them is input delay — the part of the wait that contention adds
      // before our code sees the tap at all, and the part a click-anchored measurement hides.
      const tapAt = this._tapOrigin();
      const tCaptureStart = performance.now();
      const perf = {
        tapAt,
        t_input_delay: Math.round(tCaptureStart - tapAt),
        ...this._captureCovariables(),
      };
      this._captureInFlight = true;
      // The button locks at the TAP — a second tap while the sensor is working would be a second
      // photo — but the cooldown RING does not start here. The ring is a countdown of the window
      // in which a second photo would be a duplicate, and that window only opens once the first
      // photo exists. In imageCapture mode the sensor takes ~1.35s, so starting the ring at the
      // tap had it 54% of the way round by the time the shutter even sounded: it counted down a
      // wait that had not begun, and the operator lost the only cue that says "not yet".
      this.captureCooldown = true;
      this.isCapturing = true;
      this.captureStatusMessage = '';
      this.$emit('capture-cooldown-active', true);
      this.$emit('capture-in-progress', true);
      // No backstop timer of its own: the capture safety timeout below is armed in this same task,
      // for the same event, and already releases the shutter. Two timers racing to undo the same
      // state is how they drift apart.

      // Safety timeout: if capture hangs (e.g. Android HAL issue),
      // force-reset the capturing state so the UI never gets stuck.
      // Extended to cover the worst-case retry path: 3 captures + 2 detection passes + 2 short waits.
      const safetyTimeout = setTimeout(() => {
        if (this.isCapturing) {
          logger.warning('Capture safety timeout: resetting isCapturing after 10s');
          this.isCapturing = false;
          this.captureStatusMessage = '';
          this.$emit('capture-in-progress', false);
        }
        // Also the backstop for the shutter lock: no photo ever landed, so no window opened, and
        // without this the button would stay locked for the rest of the shift.
        if (!this.cooldownRunning) this._releaseCooldown();
      }, 10000);

      try {
        // Let the tap's own answer reach the screen BEFORE the frame is copied. The copy
        // (createImageBitmap of the video, in _freezeFrameBitmap) runs on the main thread, and it
        // used to run in the same task as the tap: the button stayed pressed and unlocked for as
        // long as the copy took, and only then jumped to its locked state — the «frozen» shutter
        // (13-oct-2026; measured locally at ×4 CPU: a 175 ms frame with the button still pressed,
        // the same in master). One frame later the release and the lock are on screen, and the
        // copy blocks a main thread that has nothing left to draw. The race keeps a hidden tab,
        // where no frame comes, from holding the capture.
        const tPaint = performance.now();
        await Promise.race([
          new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0))),
          new Promise((resolve) => setTimeout(resolve, 100)),
        ]);
        perf.t_paint_yield = Math.round(performance.now() - tPaint);

        // Ensure video has a decodable frame before any capture path
        const tVideoWait = performance.now();
        const videoReady = await this.waitForVideoReady();
        // Polled at 50ms and can burn up to 2s before any encode happens: a "slow photo" that has
        // nothing to do with encoding, and that a second tap would appear to "fix".
        perf.t_video_ready = Math.round(performance.now() - tVideoWait);
        if (!videoReady) {
          logger.error('capturePhoto: video has no decodable frame, aborting');
          clearTimeout(safetyTimeout);
          this.isCapturing = false;
          this.$emit('capture-in-progress', false);
          this._releaseCooldown();
          return;
        }

        // The order the shutter fired on, taken at the tap: on the canvas path we release isCapturing
        // before the async encode, so the operator could switch orders mid-encode, and since the paint
        // yield above even «Atrás» can land before this line (selectedOrder is then null). The photo
        // is attributed to the order that was open when the shutter fired, not to whatever is open now.
        const orderId = fxOrderId;
        const blackDetectionEnabled = this.settings.blackImageDetection !== false;

        // Two capture SOURCES, one post-process. The remote setting (deviceSettings.js, cameraCaptureMethod)
        // chooses per client/device:
        //  - canvas ('auto'/'canvas', default): fast + robust; captures the preview frame. Shutter feedback
        //    fires at drawImage (~24ms), BEFORE the encode -> instant.
        //  - imageCapture: the real sensor still (takePhoto), which on some devices covers MORE surface than
        //    the preview stream (the reason the mode exists). Slower (~1.2s HAL reconfig), so feedback fires
        //    when the pixels actually arrive, never at the tap.
        // Both converge on captureCanvas -> _isCaptureCanvasBlack -> _fireShutterFeedback -> _encodeCaptureCanvas.
        const captureMethod = this.settings.cameraCaptureMethod || 'auto';
        // imageCaptureBlocked wins over the setting: this device already killed the renderer inside
        // takePhoto once, and honouring the setting again would only do it a second time. The
        // setting is left untouched (it is fleet-wide config, not a device verdict) — we just route
        // around it here, and the forensic mode says which path actually ran.
        const useImageCapture = captureMethod === 'imageCapture' && this.imageCapture && !this.imageCaptureBlocked;
        // T0 acknowledgement, here rather than inside _captureViaImageCapture: there it fired
        // after waitForVideoReady and the route decision, ~150ms after the finger, which is late
        // for something whose only job is to say "I got your tap". Only on the ImageCapture path —
        // a canvas capture reaches the real shutter buzz in ~24ms and the two would merge.
        if (useImageCapture) navigator.vibrate?.(35);
        reportForensicEvent('capture_start', {
          interaction_id: this._fxInteractionId,
          order_id: orderId,
          mode: useImageCapture ? 'imageCapture' : 'canvas',
          imagecapture_blocked: this.imageCaptureBlocked || undefined,
        });
        perf.mode = useImageCapture ? 'imageCapture' : 'canvas';
        if (useImageCapture) {
          await this._captureViaImageCapture(orderId, blackDetectionEnabled, safetyTimeout, tapAt, perf);
        } else {
          await this._captureViaCanvas(orderId, blackDetectionEnabled, safetyTimeout, tapAt, perf);
        }

      } catch (error) {
        clearTimeout(safetyTimeout);
        this.isCapturing = false;
        this.captureStatusMessage = '';
        this.$emit('capture-in-progress', false);
        // Only if the anti-duplicate window never opened. A throw AFTER the photo landed (in
        // _emitPhoto, or in the parent's handler) would otherwise cut the ring short and hand the
        // shutter back immediately — the exact double capture the window exists to prevent.
        if (!this.cooldownRunning) this._releaseCooldown();
        logger.error('Error capturing photo:', {error: error});
      } finally {
        this._captureInFlight = false;
      }
    },
    /**
     * CANVAS source: capture the preview frame. Pixels freeze at the ImageBitmap/drawImage (~24ms);
     * shutter feedback fires BEFORE the encode so it feels instant, and black retries only pay
     * freeze+detect (not encode).
     *
     * Two encode routes, same post-process semantics:
     *   worker (default) — freeze into a transferable ImageBitmap, hand it to the encode worker,
     *     which answers the black verdict first (that is what gates the shutter) and the JPEG after.
     *     Measured on a Honor Pad X9A: 62.8ms -> 9.3ms of main-thread blocking, same 333KB JPEG.
     *   main thread (`forceMainThread`, or no worker) — the original draw + toBlob path, kept
     *     verbatim because it is the safety net for a browser without OffscreenCanvas and for a
     *     worker that dies mid-shift.
     */
    async _captureViaCanvas(orderId, blackDetectionEnabled, safetyTimeout, tCaptureStart, perf = {}, forceMainThread = false) {
      const encoder = forceMainThread ? null : this._photoEncoder;
      const useWorker = !!(encoder && encoder.isReady());
      // Why the worker was NOT used is as diagnostic as the timings: "this model never supports
      // it" and "the first photo of the shift beat the chunk download" are different problems.
      if (!perf.route) {
        perf.route = useWorker ? 'worker'
          : forceMainThread ? 'fallback_verdict'
            : !encoder ? 'main_unsupported'
              : encoder.isAvailable() ? 'main_not_ready' : 'main_after_failure';
      }

      let dims = null;
      let isBlack = false;
      let attemptsUsed = 0;
      let capturedAt = null;
      let frozenAt = null;
      let job = null;

      for (let attempt = 0; attempt <= BLACK_DETECT_MAX_RETRIES; attempt++) {
        attemptsUsed = attempt + 1;
        if (attempt > 0) {
          // Retry: tell the operator why there is a brief pause, and give the sensor a moment to re-expose.
          this.captureStatusMessage = this.$t('message.retryingCapture');
          await new Promise((resolve) => setTimeout(resolve, BLACK_DETECT_RETRY_DELAY_MS));
        }
        if (useWorker) {
          // A retry costs one extra worker round trip (~1ms) on top of the freeze — the verdict
          // message is sent before any encode, so a black frame is never encoded and never waits
          // on one. That is negligible next to the 150ms re-expose pause the retry already takes.
          let frozen = null;
          try {
            const tFreeze = performance.now();
            frozen = await this._freezeFrameBitmap();
            capturedAt = frozen.capturedAt;
            frozenAt = frozen.frozenAt;
            dims = frozen.dims;
            perf.t_freeze = Math.round(performance.now() - tFreeze);
            job = encoder.submit({ bitmap: frozen.bitmap, ...this._encodeJobOptions(blackDetectionEnabled) });
            const verdict = await job.verdict;
            isBlack = verdict.isBlack;
            perf.t_verdict = Math.round(performance.now() - tCaptureStart);
            perf.worker_verdict_ms = verdict.verdictMs ?? null;
          } catch (error) {
            this._closeBitmapIfOwned(frozen);
            // beforeUnmount terminates the worker and fails its in-flight jobs; retaking against
            // a torn-down camera would only throw again and report a fault that isn't one.
            if (this.isUnmounted) return;
            perf.route = 'fallback_verdict'; // set here, not in the recursive call: perf.route is already 'worker'
            logger.warning('Encode worker failed before the shutter, retaking on the main thread', { error: String(error?.message || error) });
            reportForensicEvent('capture_encode_fallback', {
              interaction_id: this._fxInteractionId,
              order_id: orderId,
              stage: 'verdict',
            });
            return await this._captureViaCanvas(orderId, blackDetectionEnabled, safetyTimeout, tCaptureStart, perf, true);
          }
        } else {
          const tDraw = performance.now();
          dims = this._drawCaptureCanvas();
          capturedAt = new Date().toISOString();
          frozenAt = performance.now();
          isBlack = blackDetectionEnabled ? this._isCaptureCanvasBlack() : false;
          perf.t_freeze = Math.round(frozenAt - tDraw);
        }
        if (!isBlack) break;
        logger.warning(`Black image detected on attempt ${attempt + 1}/${BLACK_DETECT_MAX_RETRIES + 1}`);
      }

      this.captureStatusMessage = '';
      clearTimeout(safetyTimeout);

      if (isBlack) {
        this.isCapturing = false;
        this.$emit('capture-in-progress', false);
        // No photo exists, so there is no duplicate to prevent: give the shutter straight back
        // instead of making the operator wait out a cooldown for a capture that failed.
        this._releaseCooldown();
        logger.error(`capturePhoto: all ${BLACK_DETECT_MAX_RETRIES + 1} attempts produced black images, rejecting`);
        reportForensicEvent('capture_black_rejected', {
          interaction_id: this._fxInteractionId,
          order_id: orderId,
          attempts: BLACK_DETECT_MAX_RETRIES + 1,
        });
        this.$emit('black-image-detected');
        return;
      }

      // Pixels frozen and non-black: instant "photo taken" feedback NOW, before the encode.
      this._fireShutterFeedback();
      this._startCooldownWindow();
      // What the person actually feels: finger down to clack/flash. The single number to watch.
      perf.t_shutter = Math.round(performance.now() - tCaptureStart);
      this.isCapturing = false;
      this.$emit('capture-in-progress', false);

      const tEncodeStart = performance.now();
      let blob;
      if (useWorker) {
        const result = await this._awaitWorkerBlob(job, orderId, frozenAt, perf);
        blob = result.blob;
        if (result.dims) dims = result.dims; // the main-thread fallback redrew; trust its geometry
        perf.worker_draw_ms = result.drawMs ?? null;
        perf.worker_encode_ms = result.encodeMs ?? null;
      } else {
        blob = await this._encodeCaptureCanvas();
      }
      const encodeMs = Math.round(performance.now() - tEncodeStart);
      perf.t_encode_rtt = encodeMs;
      // THE measurement of main-thread contention, and it costs nothing: the worker reports how
      // long it was busy, we know how long the answer took to reach us, and the difference is how
      // long that answer sat in the queue waiting for a main thread that was doing something else.
      // It also separates two causes people confuse: if worker_encode_ms grows, the SoC is the
      // problem (thermal, zRAM); if only this gap grows, the main thread is.
      if (perf.worker_encode_ms != null) {
        perf.main_blocked_ms = Math.max(0, encodeMs - perf.worker_encode_ms - (perf.worker_draw_ms || 0));
      }
      // The encode now outlives the tap by design, so the component can be gone by the time it
      // lands (operator left the camera screen). Nothing downstream is listening; say nothing.
      if (this.isUnmounted) return;
      if (!blob) {
        logger.error('capturePhoto: canvas encode produced no blob after feedback');
        reportForensicEvent('capture_encode_failed', { interaction_id: this._fxInteractionId, order_id: orderId });
        this.$emit('capture-encode-failed');
        return;
      }
      this._emitPhoto(blob, dims.width, dims.height, orderId, capturedAt, perf);

      // Telemetry (throttled; only when abnormally slow, so fleet volume stays tiny).
      const tapToBlobMs = Math.round(performance.now() - tCaptureStart);
      if (tapToBlobMs > CAPTURE_SLOW_THRESHOLD_MS) {
        logger.warning('camera_capture_slow', {
          tapToBlobMs, encodeMs, mode: useWorker ? 'canvas-worker' : 'canvas', attempts: attemptsUsed,
          output: dims ? `${dims.width}x${dims.height}` : null,
          deviceMemory: navigator.deviceMemory || null,
        });
      }
    },
    /**
     * Reads the crash guard left by a previous run. A mark still in localStorage means the page went
     * away between "about to call takePhoto" and "takePhoto returned" — and the only thing that
     * removes a page mid-call without letting any handler run is the renderer being killed. Latch
     * the verdict so this device never takes that path again, and report it: this is the one signal
     * that tells us a HAL is hostile, and it can only ever be sent from the run *after* the crash.
     */
    _resolveImageCaptureGuard() {
      let mark = null;
      try {
        mark = localStorage.getItem(IMAGECAPTURE_INFLIGHT_KEY);
        this._applyImageCaptureVerdict(localStorage.getItem(IMAGECAPTURE_VERDICT_KEY));
      } catch (error) {
        // Storage disabled/full. Without it the guard cannot work, but capture must still run.
        logger.console('[CAMERA] ImageCapture guard unavailable (storage)', error);
        return;
      }
      if (!mark) return;

      // A mark this very JS context wrote is not evidence of anything: the renderer is demonstrably
      // alive (we are running). It only means a takePhoto was still pending when the component was
      // torn down — opening Settings or Help does exactly that, and so does a HAL call that never
      // settles. Clear it and move on; latching here would blame a healthy device.
      if (mark === inflightMarkFromThisContext) {
        inflightMarkFromThisContext = null;
        try { localStorage.removeItem(IMAGECAPTURE_INFLIGHT_KEY); } catch { /* best effort */ }
        logger.console('[CAMERA] stale in-flight mark from this context (re-mount, not a crash)');
        return;
      }

      // A mark from a previous context: the page went away mid-call without running a single
      // handler — not even pagehide, which we clear on. That is the SIGKILL signature.
      let parsed = {};
      try { parsed = JSON.parse(mark) || {}; } catch { /* pre-JSON or truncated mark */ }
      const verdict = parsed.explicit ? VERDICT_BLOCKED : VERDICT_EXPLICIT;
      this._applyImageCaptureVerdict(verdict);
      try {
        localStorage.removeItem(IMAGECAPTURE_INFLIGHT_KEY);
        localStorage.setItem(IMAGECAPTURE_VERDICT_KEY, verdict);
      } catch { /* best effort: the in-memory flags already protect this session */ }

      // ageMs is what tells a crash-then-reload (seconds) from a hang-then-reload (minutes) after
      // the fact, and the only way to measure how often this guard fires on healthy devices.
      const ageMs = parsed.at ? Date.now() - Date.parse(parsed.at) : null;
      // error, not warning: a device that kills the browser on a supported API is a hardware fault
      // we want surfaced in Error Tracking, once, with enough context to identify the model.
      logger.error('camera_imagecapture_crashed_renderer', {
        verdict,
        explicit: !!parsed.explicit,
        stream: parsed.stream || null,
        ageMs,
        userAgent: navigator.userAgent,
        deviceMemory: navigator.deviceMemory || null,
      });
      reportForensicEvent('capture_imagecapture_demoted', {
        verdict, explicit: !!parsed.explicit, stream: parsed.stream || null, age_ms: ageMs,
      });
    },
    /** Applies a stored/derived verdict to the in-memory flags. Unknown values are ignored. */
    _applyImageCaptureVerdict(verdict) {
      if (verdict === VERDICT_BLOCKED) {
        this.imageCaptureBlocked = true;
        this.imageCaptureNeedsExplicitSize = true;
      } else if (verdict === VERDICT_EXPLICIT) {
        this.imageCaptureNeedsExplicitSize = true;
      }
    },
    /** Drops the in-flight mark on an orderly teardown. A SIGKILL never gets here — that asymmetry
     *  IS the detector: whatever fires pagehide left on purpose and must not be read as a crash. */
    _clearInflightMark() {
      inflightMarkFromThisContext = null;
      try { localStorage.removeItem(IMAGECAPTURE_INFLIGHT_KEY); } catch { /* best effort */ }
    },
    /**
     * takePhoto() wrapped in the crash mark. Everything between setItem and removeItem is the window
     * in which a hostile HAL can take the renderer down; if that happens the mark is what the next
     * run finds. Synchronous writes on purpose — an await here would defeat the whole mechanism.
     */
    async _takePhotoGuarded(photoSettings) {
      const trackSettings = this.videoTrack?.getSettings?.();
      const mark = JSON.stringify({
        at: new Date().toISOString(),
        stream: trackSettings ? `${trackSettings.width}x${trackSettings.height}` : null,
        explicit: !!photoSettings,
      });
      inflightMarkFromThisContext = mark;
      try { localStorage.setItem(IMAGECAPTURE_INFLIGHT_KEY, mark); } catch { /* guard is best effort */ }
      try {
        return await (photoSettings ? this.imageCapture.takePhoto(photoSettings) : this.imageCapture.takePhoto());
      } finally {
        // Only clear OUR mark: after the 10s safety timeout a second tap can start an overlapping
        // capture, and blindly removing would erase the newer call's mark (losing its crash signal).
        try {
          if (localStorage.getItem(IMAGECAPTURE_INFLIGHT_KEY) === mark) {
            localStorage.removeItem(IMAGECAPTURE_INFLIGHT_KEY);
          }
        } catch { /* idem */ }
        if (inflightMarkFromThisContext === mark) inflightMarkFromThisContext = null;
      }
    },
    /**
     * Second chance for a HAL that returned an undecodable blob: ask again, this time naming the
     * exact geometry. The stream's own dimensions are the safest request — they are, by definition,
     * a mode the driver already has configured and running, which is what the broken ones choke on
     * when left to pick for themselves. Returns the decoded bitmap, or null to fall back to canvas.
     */
    async _retryTakePhotoWithExplicitSize() {
      const photoSettings = this._explicitPhotoSettings();
      if (!photoSettings) return null;
      try {
        const blob = await this._takePhotoGuarded(photoSettings);
        const bitmap = await createImageBitmap(blob, { resizeWidth: this.maxPhotoWidth, resizeQuality: 'high' });
        // Remember it, here and across restarts: on this device the implicit call is dead weight,
        // and every later photo can go straight to the one that works.
        this.imageCaptureNeedsExplicitSize = true;
        try { localStorage.setItem(IMAGECAPTURE_VERDICT_KEY, VERDICT_EXPLICIT); } catch { /* best effort */ }
        logger.warning('ImageCapture recovered with explicit photoSettings', {
          requested: `${photoSettings.imageWidth}x${photoSettings.imageHeight}`,
          got: `${bitmap.width}x${bitmap.height}`,
        });
        return bitmap;
      } catch (error) {
        logger.warning('ImageCapture retry with explicit photoSettings also failed, falling back to canvas', error);
        return null;
      }
    },
    /** The stream's own geometry: by definition a mode the driver already has configured and
     *  running, which is exactly what the broken HALs choke on when left to choose for themselves. */
    _explicitPhotoSettings() {
      const settings = this.videoTrack?.getSettings?.();
      if (!settings?.width || !settings?.height) return null;
      return { imageWidth: settings.width, imageHeight: settings.height };
    },
    /**
     * One FULL still for the periscope tutorial's live view (PeriscopeTutorialScreen): the whole sensor,
     * uncropped and unflipped, because that is what shows whether the mirror lets anything else in — the
     * preview stream covers less than the photo does. Same takePhoto and the same _takePhotoGuarded as a
     * real shot, so the in-flight mark that protects the devices whose HAL kills the renderer is written
     * and cleared here too; and nothing else of a shot: no sound, vibration or flash, no cooldown, no
     * `photo-captured`, no forensic event, no upload. The caller owns the pacing (one call at a time).
     * Returns { blob, source, width, height }: source 'image_capture', or 'canvas' when takePhoto is
     * missing, blocked or unusable here (an undecodable blob, or the portrait frame of the A16) and the
     * blob is the stream's own full frame instead.
     */
    async captureFullFrame() {
      if (!this.cameraReady) throw new Error('captureFullFrame: the camera is not ready');
      if (this.imageCapture && !this.imageCaptureBlocked) {
        try {
          const photoSettings = this.imageCaptureNeedsExplicitSize ? this._explicitPhotoSettings() : null;
          const blob = await this._takePhotoGuarded(photoSettings);
          const bitmap = await createImageBitmap(blob);
          const { width, height } = bitmap;
          if (bitmap.close) bitmap.close();
          if (width >= height) return { blob, source: 'image_capture', width, height };
          logger.warning(`captureFullFrame: ImageCapture returned portrait ${width}x${height} (driver rotation); using the stream frame`);
        } catch (error) {
          logger.warning('captureFullFrame: takePhoto unusable, using the stream frame', error);
        }
      }
      const video = this.previewVideo;
      const width = video?.videoWidth || 0;
      const height = video?.videoHeight || 0;
      if (!width || !height) throw new Error('captureFullFrame: the stream has no decodable frame');
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(video, 0, 0, width, height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
      canvas.width = 0; // let go of the pixels now, not at the next GC
      canvas.height = 0;
      if (!blob) throw new Error('captureFullFrame: the canvas produced no image');
      return { blob, source: 'canvas', width, height };
    },
    /**
     * IMAGECAPTURE source (remote opt-in): the real sensor still (takePhoto), which on some devices covers
     * more surface than the preview stream. NEVER fires the shutter at the tap — the pixels don't exist
     * until takePhoto resolves (~1.2s), and a premature flash/clac would make the operator move the tablet
     * and blur the shot. T0 = a short distinct vibrate ("working"); T1 (pixels ready) = full shutter feedback.
     * Converges on the same captureCanvas post-process as the canvas path. Falls back to canvas on any
     * failure or on a driver that returns a rotated (portrait) frame (Samsung A16).
     */
    async _captureViaImageCapture(orderId, blackDetectionEnabled, safetyTimeout, tCaptureStart, perf = {}) {
      const encoder = this._photoEncoder;
      const useWorker = !!(encoder && encoder.isReady());
      perf.route = useWorker ? 'worker' : (encoder?.isAvailable() ? 'main_not_ready' : 'main_unsupported');
      let dims = null;
      let isBlack = false;
      let capturedAt = null;
      let frozenAt = null;
      let job = null;
      for (let attempt = 0; attempt <= BLACK_DETECT_MAX_RETRIES; attempt++) {
        if (attempt > 0) {
          this.captureStatusMessage = this.$t('message.retryingCapture');
          await new Promise((resolve) => setTimeout(resolve, BLACK_DETECT_RETRY_DELAY_MS));
        }
        // Implicit by default (native full-sensor FOV — the reason this mode exists), explicit only
        // on a device already known to mishandle the implicit call. Skipping it there is not just
        // tidiness: it is a wasted ~1.2s HAL round trip and a 5.4MB throwaway buffer PER PHOTO on a
        // 4GB tablet, and two of those per black-detect retry would push the worst case into the
        // 10s safety timeout.
        const explicitSettings = this.imageCaptureNeedsExplicitSize ? this._explicitPhotoSettings() : null;
        let blob;
        const tTakePhoto = performance.now();
        try {
          blob = await this._takePhotoGuarded(explicitSettings);
        } catch (error) {
          logger.warning('ImageCapture.takePhoto failed, falling back to canvas', error);
          return await this._captureViaCanvas(orderId, blackDetectionEnabled, safetyTimeout, tCaptureStart, perf);
        }
        // Decode + downscale to maxPhotoWidth in one native step: preserves the full sensor FOV (rescale,
        // not crop) while avoiding the ~32MB full-res RGBA peak on 4GB devices.
        perf.t_takephoto = Math.round(performance.now() - tTakePhoto);
        perf.sensor_bytes = blob?.size ?? null;

        // FAST PATH: hand the sensor's JPEG straight to the worker, undecoded.
        //
        // createImageBitmap looks like free background work, but it decodes on a helper thread and
        // then does the RESIZE and the final copy on the calling thread. Measured on a Honor Pad
        // X9A, that was the one ~275ms long animation frame present in every single capture — and
        // it carried no script attribution at all (LoAF named 5-25ms of JavaScript out of 230ms),
        // because it is browser work, not ours. Decoding in the worker moves that calling thread
        // off main and takes the 20MB RGBA peak with it. The Blob crosses by reference: no copy.
        if (useWorker) {
          const outcome = await this._captureSensorBlobViaWorker(blob, blackDetectionEnabled, perf);
          if (outcome.retryExplicit) {
            logger.warning('Worker could not decode the ImageCapture blob, retrying with explicit photoSettings');
            const retried = await this._retryTakePhotoWithExplicitSize();
            if (!retried) {
              return await this._captureViaCanvas(orderId, blackDetectionEnabled, safetyTimeout, tCaptureStart, perf);
            }
            if (retried.close) retried.close();
            return await this._captureViaCanvas(orderId, blackDetectionEnabled, safetyTimeout, tCaptureStart, perf);
          }
          if (outcome.fallbackToCanvas) {
            return await this._captureViaCanvas(orderId, blackDetectionEnabled, safetyTimeout, tCaptureStart, perf);
          }
          job = outcome.job;
          dims = outcome.dims;
          isBlack = outcome.isBlack;
          capturedAt = new Date().toISOString();
          frozenAt = performance.now();
          if (!isBlack) break;
          logger.warning(`Black image detected on attempt ${attempt + 1}/${BLACK_DETECT_MAX_RETRIES + 1} (ImageCapture)`);
          continue;
        }

        // MAIN-THREAD PATH (no worker): decode here, as before.
        let bitmap;
        const tDecode = performance.now();
        try {
          bitmap = await createImageBitmap(blob, { resizeWidth: this.maxPhotoWidth, resizeQuality: 'high' });
        } catch (error) {
          // The HAL answered, but with something that is not a decodable image. Measured on the Honor
          // Pad X9A, alternating both call shapes on a freshly started browser:
          //   1. takePhoto()            -> 5566 KB, undecodable
          //   2. takePhoto({w,h})       -> 3018 KB, fine (2592x1168)
          //   3. takePhoto()            -> 3021 KB, fine   <- the implicit call works now
          //   4-6. both shapes          -> fine
          // So the defect is not "takePhoto is broken" but "the FIRST call of a camera session, made
          // without photoSettings, leaves the JPEG pipeline misconfigured" — and every later call
          // inherits it until something names a geometry. One explicit call repairs the session, so
          // retrying here does not just rescue this photo: it fixes the ones after it. Dropping
          // straight to canvas would work too, but would silently give up the extra sensor surface
          // this mode exists to capture.
          if (explicitSettings) {
            // Already asked for a named geometry and it still came back undecodable: this HAL has
            // nothing left to offer. Canvas from here.
            logger.warning('ImageCapture returned an undecodable blob even with explicit photoSettings, falling back to canvas', error);
            return await this._captureViaCanvas(orderId, blackDetectionEnabled, safetyTimeout, tCaptureStart, perf);
          }
          logger.warning('createImageBitmap failed on ImageCapture blob, retrying with explicit photoSettings', error);
          bitmap = await this._retryTakePhotoWithExplicitSize();
          if (!bitmap) {
            return await this._captureViaCanvas(orderId, blackDetectionEnabled, safetyTimeout, tCaptureStart, perf);
          }
        }
        // A16 guard: some drivers return a PORTRAIT still for the back camera (the Amrest bug). takePhoto()
        // does NOT throw in that case — it rotates — so the try/catch above doesn't cover it. We expect
        // landscape; if it came back taller than wide, fall back to canvas (safe default). A rotate-correct
        // path can be added later, but only after QA on a real A16.
        if (bitmap.width < bitmap.height) {
          logger.warning(`ImageCapture returned portrait ${bitmap.width}x${bitmap.height} (driver rotation, e.g. A16); falling back to canvas`);
          if (bitmap.close) bitmap.close();
          return await this._captureViaCanvas(orderId, blackDetectionEnabled, safetyTimeout, tCaptureStart, perf);
        }
        perf.t_decode = Math.round(performance.now() - tDecode);
        dims = this._drawBitmapFull(bitmap);
        if (bitmap.close) bitmap.close(); // free the RGBA immediately (memory peak on 4GB)
        isBlack = blackDetectionEnabled ? this._isCaptureCanvasBlack() : false;
        capturedAt = new Date().toISOString();
        frozenAt = performance.now();
        if (!isBlack) break;
        logger.warning(`Black image detected on attempt ${attempt + 1}/${BLACK_DETECT_MAX_RETRIES + 1} (ImageCapture)`);
      }

      this.captureStatusMessage = '';
      clearTimeout(safetyTimeout);

      if (isBlack) {
        this.isCapturing = false;
        this.$emit('capture-in-progress', false);
        this._releaseCooldown();
        logger.error(`capturePhoto: all ${BLACK_DETECT_MAX_RETRIES + 1} ImageCapture attempts produced black images, rejecting`);
        reportForensicEvent('capture_black_rejected', {
          interaction_id: this._fxInteractionId,
          order_id: orderId,
          attempts: BLACK_DETECT_MAX_RETRIES + 1,
        });
        this.$emit('black-image-detected');
        return;
      }

      // T1: pixels are ready -> shutter feedback + release the capturing indicator.
      this._fireShutterFeedback();
      this._startCooldownWindow();
      perf.t_shutter = Math.round(performance.now() - tCaptureStart);
      this.isCapturing = false;
      this.$emit('capture-in-progress', false);

      let finalBlob;
      if (useWorker) {
        const tEncode = performance.now();
        const result = await this._awaitWorkerBlob(job, orderId, frozenAt, perf);
        finalBlob = result.blob;
        if (result.dims) dims = result.dims; // the main-thread fallback redrew from the preview
        perf.t_encode_rtt = Math.round(performance.now() - tEncode);
        perf.worker_draw_ms = result.drawMs ?? null;
        perf.worker_encode_ms = result.encodeMs ?? null;
        if (perf.worker_encode_ms != null) {
          perf.main_blocked_ms = Math.max(0, perf.t_encode_rtt - perf.worker_encode_ms - (perf.worker_draw_ms || 0));
        }
      } else {
        finalBlob = await this._encodeCaptureCanvas();
      }
      if (this.isUnmounted) return;
      if (!finalBlob) {
        logger.error('capturePhoto: ImageCapture encode produced no blob after feedback');
        reportForensicEvent('capture_encode_failed', { interaction_id: this._fxInteractionId, order_id: orderId });
        this.$emit('capture-encode-failed');
        return;
      }
      this._emitPhoto(finalBlob, dims.width, dims.height, orderId, capturedAt, perf);
      // No slow-telemetry here: ImageCapture is expected to be slow by design (HAL reconfig); emitting it
      // every shot would flood the throttle bucket. Canvas is the path we watch for anomalies.
    },
    /**
     * Hands a sensor JPEG to the worker and waits for its black verdict, without ever decoding it
     * on this thread. Returns what the caller should do next:
     *   { job, dims, isBlack }   — decoded fine, carry on
     *   { retryExplicit: true }  — undecodable; one explicit takePhoto repairs the session on some HALs
     *   { fallbackToCanvas: true } — portrait frame (A16 drivers) or the worker went away
     */
    async _captureSensorBlobViaWorker(blob, blackDetectionEnabled, perf) {
      try {
        const job = this._photoEncoder.submit({
          blob,
          resizeWidth: this.maxPhotoWidth,
          // The portrait check has to happen where the frame is decoded, which is now the worker.
          expectLandscape: true,
          ...this._encodeJobOptions(blackDetectionEnabled),
        });
        const verdict = await job.verdict;
        perf.t_decode = verdict.decodeMs ?? null;
        perf.t_verdict = Math.round(performance.now() - (perf.tapAt ?? performance.now()));
        perf.worker_verdict_ms = verdict.verdictMs ?? null;
        return { job, dims: { width: verdict.width, height: verdict.height }, isBlack: verdict.isBlack };
      } catch (error) {
        if (error?.reason === 'decode') return { retryExplicit: true };
        if (error?.reason === 'portrait') {
          logger.warning(`ImageCapture returned portrait ${error.width}x${error.height} (driver rotation, e.g. A16); falling back to canvas`);
          return { fallbackToCanvas: true };
        }
        if (this.isUnmounted) return { fallbackToCanvas: true };
        perf.route = 'fallback_verdict';
        logger.warning('Encode worker failed on the ImageCapture frame, retaking on the main thread', { error: String(error?.message || error) });
        reportForensicEvent('capture_encode_fallback', {
          interaction_id: this._fxInteractionId,
          order_id: this.selectedOrder?.orderId || null,
          stage: 'verdict_imagecapture',
        });
        return { fallbackToCanvas: true };
      }
    },
    /**
     * Draws a full sensor frame (an ImageBitmap already downscaled to maxPhotoWidth by createImageBitmap)
     * into captureCanvas with flips. No cover-crop: the ImageCapture mode exists to keep the maximum
     * captured surface, so the whole sensor frame is drawn.
     */
    _drawBitmapFull(bitmap) {
      const tw = bitmap.width, th = bitmap.height;
      if (this.captureCanvas.width !== tw || this.captureCanvas.height !== th) {
        this.captureCanvas.width = tw;
        this.captureCanvas.height = th;
      }
      const ctx = this.captureContext;
      ctx.save();
      if (this.settings.flipHorizontally) { ctx.scale(-1, 1); ctx.translate(-tw, 0); }
      if (this.settings.flipVertically) { ctx.scale(1, -1); ctx.translate(0, -th); }
      ctx.drawImage(bitmap, 0, 0, tw, th);
      ctx.restore();
      this._lastCaptureDims = { width: tw, height: th };
      return this._lastCaptureDims;
    },
    /**
     * MAIN-THREAD path. Draws the current video frame into this.captureCanvas, applying the exact
     * same visible-region crop (object-fit: cover, unless fullFrame) and flips as before, and
     * returns the target dimensions. The capture pipeline can then (1) freeze the pixels, (2) run
     * cheap black-detection on the canvas, and (3) fire shutter feedback — all BEFORE the expensive
     * toBlob encode. Pixels are effectively "frozen" the instant this returns (~24ms measured on a
     * Redmi Pad Pro).
     *
     * Only used when the encode worker is unavailable or has failed; the normal path freezes into
     * an ImageBitmap instead (_freezeFrameBitmap) and never touches this canvas.
     */
    _drawCaptureCanvas() {
      const geometry = this._computeCaptureGeometry();
      const { sx, sy, sWidth, sHeight, targetWidth, targetHeight, frameWidth, frameHeight } = geometry;

      logger.console(`Capturing visible region sx=${sx} sy=${sy} ${sWidth}x${sHeight} from frame ${frameWidth}x${frameHeight}, target ${targetWidth}x${targetHeight}`);

      // Only reallocate the canvas backing store when the target dims actually change. Assigning
      // canvas.width/height ALWAYS reallocs + clears the backing store (~11MB RGBA at 1920px), and
      // dims are stable across shots for a given order/orientation — so re-assigning every shot just
      // feeds GC/alloc churn on 4GB devices. The full-frame drawImage below overwrites every pixel,
      // so skipping the realloc leaves no stale content.
      if (this.captureCanvas.width !== targetWidth || this.captureCanvas.height !== targetHeight) {
        this.captureCanvas.width = targetWidth;
        this.captureCanvas.height = targetHeight;
      }

      this.captureContext.save();

      // Apply any necessary transformations
      if (this.settings.flipHorizontally) {
        this.captureContext.scale(-1, 1);
        this.captureContext.translate(-targetWidth, 0);
      }
      if (this.settings.flipVertically) {
        this.captureContext.scale(1, -1);
        this.captureContext.translate(0, -targetHeight);
      }

      // Draw only the visible region from the raw frame, matching what the user sees in the preview.
      this.captureContext.drawImage(this.previewVideo, sx, sy, sWidth, sHeight, 0, 0, targetWidth, targetHeight);
      this.captureContext.restore();

      this._lastCaptureDims = { width: targetWidth, height: targetHeight };
      return this._lastCaptureDims;
    },
    /**
     * Freezes the current preview frame as an ImageBitmap, ready to be TRANSFERRED to the encode
     * worker. This is the worker path's equivalent of _drawCaptureCanvas: one native call freezes,
     * crops to the visible region and downscales to the photo width, using the exact same geometry.
     *
     * Deliberately NOT createImageBitmap(this.captureCanvas): that would COPY the pixels (+11.8MB
     * measured at 1920px) on top of a canvas draw we would also be paying, on tablets with 4GB of
     * RAM. Transferring costs nothing; creating costs a full frame. Flips are not applied here —
     * the worker replays them with ctx.scale, exactly as _drawBitmapFull does today.
     *
     * The capture timestamp is stamped HERE, at the freeze, and travels with the photo. It used to
     * be stamped after the encode, which was harmless while the encode was synchronous-ish but is
     * not once encodes overlap: loadLastPhotoForOrder() sorts an order's photos by that timestamp,
     * so a late stamp is a reordered gallery.
     */
    async _freezeFrameBitmap() {
      const g = this._computeCaptureGeometry();
      const capturedAt = new Date().toISOString();
      const frozenAt = performance.now();
      const bitmap = await createImageBitmap(
        this.previewVideo, g.sx, g.sy, g.sWidth, g.sHeight,
        { resizeWidth: g.targetWidth, resizeHeight: g.targetHeight, resizeQuality: 'high' }
      );
      logger.console(`Froze visible region sx=${g.sx} sy=${g.sy} ${g.sWidth}x${g.sHeight} from frame ${g.frameWidth}x${g.frameHeight}, target ${g.targetWidth}x${g.targetHeight}`);
      return { bitmap, capturedAt, frozenAt, dims: { width: g.targetWidth, height: g.targetHeight } };
    },
    /**
     * The source rect (visible-region crop) and target size for a capture. Split out of
     * _drawCaptureCanvas so the canvas draw and the ImageBitmap freeze cannot drift apart: both
     * paths must produce the same pixels, or switching between them (worker vs fallback) would
     * change what the operator's photo shows.
     */
    _computeCaptureGeometry() {
      const videoEl = this.previewVideo;
      const frameWidth = videoEl.videoWidth || this.videoTrack.getSettings().width || this.streamWidth;
      const frameHeight = videoEl.videoHeight || this.videoTrack.getSettings().height || this.streamHeight;

      // Capture geometry MUST match what the operator sees, or the saved photo looks cropped vs the
      // preview. The preview's object-fit (set by handleResize per device) is the single source of truth:
      //   'contain' (tablet, immersive, RBE full-frame) -> preview shows the WHOLE frame -> capture it whole;
      //   'cover'   (mobile normal)                      -> preview fills+crops the box  -> capture that crop.
      // We READ the computed object-fit instead of re-deriving handleResize's breakpoint logic, so preview
      // and capture can never diverge. That exact divergence (tablet preview = contain, but capture did a
      // cover-crop) was the "las fotos salen recortadas" complaint — the operator saw the full surface but
      // we saved less of it. The cover path doubles as the Samsung Galaxy A16 workaround (its driver returns
      // portrait frames even for the back camera; cropping to the visible box hides the extra content), so
      // it stays intact on mobile — do not remove it.
      const previewIsContain = getComputedStyle(videoEl).objectFit === 'contain';
      const rect = videoEl.getBoundingClientRect();
      let sx = 0, sy = 0, sWidth = frameWidth, sHeight = frameHeight;
      if (!previewIsContain && rect.width > 0 && rect.height > 0 && frameWidth > 0 && frameHeight > 0) {
        const frameAR = frameWidth / frameHeight;
        const boxAR = rect.width / rect.height;
        if (frameAR > boxAR) {
          sHeight = frameHeight;
          sWidth = frameHeight * boxAR;
          sx = (frameWidth - sWidth) / 2;
          sy = 0;
        } else if (frameAR < boxAR) {
          sWidth = frameWidth;
          sHeight = frameWidth / boxAR;
          sx = 0;
          sy = (frameHeight - sHeight) / 2;
        }
      }

      // Integer source rect. drawImage happily takes fractional coordinates, but
      // createImageBitmap's crop rect is a WebIDL long — so without rounding here the two paths
      // would sample sub-pixel-different regions. Clamped to the frame because a rounded rect that
      // overruns the source is filled with transparent by createImageBitmap: a 1px black band down
      // the edge of every photo.
      sx = Math.round(sx);
      sy = Math.round(sy);
      sWidth = Math.min(Math.round(sWidth), frameWidth - sx);
      sHeight = Math.min(Math.round(sHeight), frameHeight - sy);

      // Target dimensions based on the visible region, preserving its aspect ratio.
      let targetWidth = sWidth;
      let targetHeight = sHeight;
      if (targetWidth > this.maxPhotoWidth) {
        const scaleFactor = this.maxPhotoWidth / targetWidth;
        targetWidth = this.maxPhotoWidth;
        targetHeight = Math.round(targetHeight * scaleFactor);
      }

      return { sx, sy, sWidth, sHeight, targetWidth, targetHeight, frameWidth, frameHeight };
    },
    /**
     * MAIN-THREAD encode of whatever is currently in this.captureCanvas. Only reached when the
     * worker is unavailable or has failed; the ladder itself lives in photoEncoderCore so both
     * paths enforce the same cap (a cap that only holds on one path is a cap that silently stops
     * holding).
     */
    async _encodeCaptureCanvas() {
      const result = await encodeCanvasWithCap(this.captureCanvas, {
        quality: this.imageQuality,
        maxBytes: MAX_PHOTO_BYTES,
        qualitySteps: RECOMPRESS_QUALITY_STEPS,
      });
      this._reportEncodeLadder(result);
      return result.blob;
    },
    /**
     * Everything the worker needs to reproduce the in-component post-process. The thresholds travel
     * in the message rather than being duplicated in the worker, so they stay defined once, here,
     * next to the measurements that justify them.
     */
    _encodeJobOptions(blackDetectionEnabled) {
      return {
        detectBlack: blackDetectionEnabled !== false,
        sampleSize: BLACK_DETECT_SAMPLE_SIZE,
        rgbSumThreshold: BLACK_DETECT_RGB_SUM_THRESHOLD,
        ratioThreshold: BLACK_DETECT_RATIO_THRESHOLD,
        flipHorizontally: !!this.settings.flipHorizontally,
        flipVertically: !!this.settings.flipVertically,
        quality: this.imageQuality,
        maxBytes: MAX_PHOTO_BYTES,
        qualitySteps: RECOMPRESS_QUALITY_STEPS,
        // Correlates the worker job with the shutter tap in the forensic timeline.
        interactionId: this._fxInteractionId || null,
      };
    },
    /**
     * Waits for the worker's JPEG. On failure the frozen frame is gone with the worker — but the
     * shutter already fired, so the operator believes a photo exists. Re-freeze from the still-live
     * preview and encode on the main thread: a frame a few ms late beats a photo that never was.
     *
     * ONLY while the scene is still the one that was shot, though. See REFREEZE_MAX_AGE_MS: past
     * that, or once the operator has moved to another order, a re-take is not a late photo of this
     * bag — it is a photo of something else, filed under this order and back-dated to this
     * capture's timestamp. That is unrecoverable and undetectable downstream, so we drop the photo
     * instead and let the caller report the failure.
     *
     * Returns { blob, dims } where dims is set only if the fallback redrew at a different size.
     */
    async _awaitWorkerBlob(job, orderId, frozenAt, perf = {}) {
      try {
        const result = await job.blob;
        this._reportEncodeLadder(result);
        return { blob: result.blob, dims: null, drawMs: result.drawMs, encodeMs: result.encodeMs };
      } catch (error) {
        // Teardown is the expected way this rejects: beforeUnmount terminates the worker and
        // fails its in-flight jobs. There is no component left to emit a photo to and no live
        // preview to re-freeze from, so leave quietly instead of logging a fault.
        if (this.isUnmounted) return { blob: null, dims: null };
        perf.route = 'fallback_encode';
        const ageMs = Math.round(performance.now() - (frozenAt ?? 0));
        const sameOrder = this.selectedOrder?.orderId === orderId;
        if (!frozenAt || ageMs > REFREEZE_MAX_AGE_MS || !sameOrder || !this.cameraReady) {
          logger.error('capturePhoto: worker encode failed too late to re-take safely, dropping the photo', {
            ageMs, sameOrder, cameraReady: this.cameraReady,
          });
          reportForensicEvent('capture_refreeze_refused', {
            interaction_id: this._fxInteractionId,
            order_id: orderId,
            age_ms: ageMs,
            same_order: sameOrder,
          });
          return { blob: null, dims: null };
        }
        logger.warning('Encode worker failed after the shutter, re-encoding on the main thread', { error: String(error?.message || error), ageMs });
        reportForensicEvent('capture_encode_fallback', {
          interaction_id: this._fxInteractionId,
          order_id: orderId,
          stage: 'encode',
          age_ms: ageMs,
        });
        try {
          const dims = this._drawCaptureCanvas();
          return { blob: await this._encodeCaptureCanvas(), dims };
        } catch (fallbackError) {
          logger.error('Main-thread re-encode after worker failure also failed', { error: fallbackError });
          return { blob: null, dims: null };
        }
      }
    },
    /**
     * Says out loud what the size-cap ladder had to do. Silent on the common path (one encode,
     * under the cap); loud when a photo is still oversized after the whole ladder, because that is
     * the shape that silently loses a Vertex analysis.
     */
    _reportEncodeLadder(result) {
      if (!result || !result.blob) return;
      if (result.overCap) {
        logger.error(`[CAMERA] Photo still ${result.blob.size}B after re-encoding (cap ${MAX_PHOTO_BYTES}B); CV analysis may be rejected`);
      } else if (result.steps > 0) {
        logger.console(`[CAMERA] Photo ${result.originalSize}B over the ${MAX_PHOTO_BYTES}B cap, re-encoded at q=${result.quality} -> ${result.blob.size}B`);
      }
    },
    /**
     * A bitmap that never reached postMessage (worker gone, submit threw) holds its RGBA until GC;
     * one that WAS transferred is already detached and closing it throws. Try, and don't care.
     */
    _closeBitmapIfOwned(frozen) {
      try {
        frozen?.bitmap?.close?.();
      } catch (e) {
        // Already transferred to the worker — it owns it now.
      }
    },
    /**
     * Boots the encode worker once per component lifetime. Called from _prewarmCapturePath (i.e. on
     * cameraReady) rather than at the first capture, because worker construction is a module fetch
     * plus parse that has no business sitting between a tap and a shutter.
     */
    _startPhotoEncoder() {
      if (this._photoEncoder?.isAvailable()) return;
      // A dead encoder is REPLACED, not kept. It dies for transient reasons too — a chunk that
      // never arrived on a restaurant's network, a job that timed out while the SoC was pinned —
      // and without this the first such hiccup would disable the worker path for the rest of the
      // visit. Reopening the camera is the natural, bounded moment to give it another go.
      this._photoEncoder?.terminate();
      this._photoEncoder = createPhotoEncoder({
        onFailure: (error) => {
          const reason = String(error?.message || error);
          logger.warning('Photo encode worker unavailable; encoding on the main thread', { reason });
          reportForensicEvent('encode_worker_failed', {
            interaction_id: this._fxInteractionId || null,
            reason: reason.slice(0, 120),
          });
        },
      });
    },
    /**
     * Cheap black-frame detection on the SOURCE canvas, BEFORE encoding. Downsamples the already-drawn
     * captureCanvas into a reused 32x32 canvas and inspects the 1024 pixels — same thresholds as
     * isImageBlack(), but WITHOUT re-decoding the full JPEG via createImageBitmap (which cost 64-119ms
     * and a ~18.7MB transient bitmap per call, per retry). Detecting pre-encode also means black
     * retries no longer pay the toBlob+decode cost (QW-B). Fail-open: any error returns false so a
     * buggy detector never blocks a legitimate capture.
     */
    _isCaptureCanvasBlack() {
      try {
        const size = BLACK_DETECT_SAMPLE_SIZE;
        if (!this._blackSampleCtx) {
          const c = document.createElement('canvas');
          c.width = size;
          c.height = size;
          // willReadFrequently keeps this tiny canvas on the CPU path for the immediate getImageData.
          this._blackSampleCtx = c.getContext('2d', { alpha: false, willReadFrequently: true });
        }
        const ctx = this._blackSampleCtx;
        ctx.drawImage(this.captureCanvas, 0, 0, size, size);
        const data = ctx.getImageData(0, 0, size, size).data;
        const totalPixels = size * size;
        let blackPixels = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] + data[i + 1] + data[i + 2] <= BLACK_DETECT_RGB_SUM_THRESHOLD) {
            blackPixels++;
          }
        }
        const ratio = blackPixels / totalPixels;
        return ratio >= BLACK_DETECT_RATIO_THRESHOLD;
      } catch (error) {
        logger.error('_isCaptureCanvasBlack: detection failed, falling open', { error: error });
        return false;
      }
    },
    /**
     * Opens the anti-duplicate window: starts the ring and schedules the unlock. Called the moment
     * a photo actually exists, alongside the shutter feedback, so what the ring counts down and
     * what the button enforces are the same 2.5 seconds.
     */
    _startCooldownWindow() {
      if (this.cooldownRunning) return;
      // The reference point for "how long since the photo", which is what a swallowed tap should
      // be measured against now that the window no longer starts at the tap.
      this._fxLastShutterAt = performance.now();
      this.cooldownRunning = true;
      this.$emit('capture-cooldown-running', true);
      clearTimeout(this._cooldownTimer);
      this._cooldownTimer = setTimeout(() => this._releaseCooldown(), COOLDOWN_AFTER_PHOTO_MS);
    },
    /** Gives the shutter back, from any path: success, rejection, failure or the backstop. */
    _releaseCooldown() {
      clearTimeout(this._cooldownTimer);
      this._cooldownTimer = null;
      this.cooldownRunning = false;
      this.$emit('capture-cooldown-running', false);
      if (!this.captureCooldown) return;
      this.captureCooldown = false;
      this.$emit('capture-cooldown-active', false);
    },
    /**
     * Fires the "photo taken" feedback synchronously. Called the instant pixels are frozen (canvas
     * path), NOT after the encode. vibrate (OS) and the shutter sound (Web Audio, preloaded buffer)
     * are immune to main-thread queueing; the flash is opacity-only (compositor-driven). Net result:
     * the shutter is felt at ~24ms from the tap regardless of how long the encode takes.
     */
    _fireShutterFeedback() {
      // Physical channels first, DOM last. Vibration and Web Audio leave the main thread the
      // moment they are called (OS and audio thread respectively), so putting them ahead of any
      // DOM work means they are never delayed by it. It costs nothing to order them this way.
      navigator.vibrate?.(100);
      this.playCameraSound();
      this.triggerFlashEffect();
    },
    /**
     * `capturedAt` is stamped when the pixels froze, NOT here. With the encode off-thread the gap
     * between freeze and emit is no longer a constant, and CameraScreen.loadLastPhotoForOrder()
     * sorts an order's photos by this timestamp — stamping it on emit would let a slow encode
     * reorder the gallery. Falls back to now() only for callers that have no freeze time.
     */
    _emitPhoto(blob, width, height, orderId, capturedAt, perf = {}) {
      const photoData = {
        key: uuidv4(),
        image: blob,
        width: width,
        height: height,
        timestamp: capturedAt || new Date().toISOString(),
        order: orderId,
        percentageUploaded: 0,
        transferredBytes: 0,
        totalBytes: 0,
        // Carry the capture interaction id so the upload lifecycle can be joined
        // back to this shutter tap in Athena.
        interactionId: this._fxInteractionId || null,
        // The tap this photo came from, so CameraScreen can time how long the "Analyse" button
        // takes to actually appear — the literal complaint from ops.
        tapAt: perf.tapAt ?? null
      };
      // Forensic terminal for a successful capture. photo_key (the uuid) is the
      // join to bronze.photo.url — the same value ends up in the S3 filename.
      const photosSoFar = notePhotoCaptured();
      const { tapAt, ...timings } = perf;
      reportForensicEvent('capture_saved', {
        interaction_id: this._fxInteractionId || null,
        order_id: orderId,
        photo_key: photoData.key,
        ...timings,
        t_emit: tapAt != null ? Math.round(performance.now() - tapAt) : null,
        bytes: blob?.size ?? null,
        w: width,
        h: height,
        photo_n: photosSoFar,
        // Who was holding the main thread during THIS capture. Absent when nothing blocked, so a
        // healthy capture stays small.
        ...(tapAt != null ? loafWindow(tapAt, performance.now()) || {} : {}),
      });
      this.$emit('photo-captured', photoData);
    },
    /**
     * Best-effort warm-up of the JPEG encode path, run once when the camera becomes ready. Encoding a
     * tiny throwaway frame pays the first-use allocation/JIT up front, so the operator's FIRST real
     * capture doesn't eat that cost. Never fires feedback nor emits a photo.
     *
     * Both routes are warmed. The worker one because that is the normal path; the main-thread
     * toBlob one because it is the fallback, and the fallback's worst case is a worker that fails
     * on the very first photo — the one shot where nothing is warm yet.
     */
    async _prewarmCapturePath() {
      this._startPhotoEncoder();
      if (!this.previewVideo) return;
      // Two independent try blocks on purpose. They warm different routes, and a preview that
      // cannot yet produce an ImageBitmap (cameraReady is set even when the frame wait timed out)
      // must not skip warming toBlob — that is the route such a device is most likely to take.
      try {
        const sw = this.previewVideo.videoWidth;
        const sh = this.previewVideo.videoHeight;
        if (this._photoEncoder?.isAvailable() && sw && sh) {
          const bitmap = await createImageBitmap(this.previewVideo, 0, 0, sw, sh,
            { resizeWidth: 320, resizeHeight: 240, resizeQuality: 'low' });
          // No deadline: this frame is queued before the worker's chunk has necessarily loaded,
          // and on a slow network its answer is legitimately seconds away. Nobody is waiting for
          // it — captures route by isReady(), so a worker that is still starting simply is not
          // used yet, and killing the encoder over a late warm-up would disable it for the visit.
          this._photoEncoder.submit({ bitmap, ...this._encodeJobOptions(true) }, { timeout: false });
        }
      } catch (e) {
        // Pre-warm is purely an optimization; any failure is safe to ignore.
      }
      try {
        const tmp = document.createElement('canvas');
        tmp.width = 320;
        tmp.height = 240;
        const ctx = tmp.getContext('2d', { alpha: false });
        ctx.drawImage(this.previewVideo, 0, 0, 320, 240);
        await new Promise((resolve) => tmp.toBlob(() => resolve(), 'image/jpeg', this.imageQuality));
      } catch (e) {
        // Same: best effort.
      }
    },
    triggerFlashEffect() {
      const flash = this.$refs.flash;
      if (!flash) return;

      // Web Animations, not a class toggle. The old version removed the class, read
      // `offsetWidth` to force a synchronous layout so the transition would restart, and added it
      // back — a forced reflow at the single worst instant in the whole capture, and one that
      // delayed everything queued behind it. The restart it bought was never needed: the shutter
      // is locked, so a second flash cannot begin while this one is running.
      //
      // The animation is opacity-only, so the compositor runs it whatever the main thread is
      // doing, and there is no timeout left behind to clear.
      if (typeof flash.animate !== 'function') {
        // Browsers without WAAPI (and happy-dom in tests) keep the old, correct-enough behaviour.
        flash.classList.add('active');
        clearTimeout(this.flashTimeout);
        this.flashTimeout = setTimeout(() => flash.classList.remove('active'), 200);
        return;
      }
      this._flashAnimation?.cancel();
      this._flashAnimation = flash.animate(
        [{ opacity: 0 }, { opacity: 0.7, offset: 0.2 }, { opacity: 0.7, offset: 0.75 }, { opacity: 0 }],
        { duration: 250, easing: 'linear' },
      );
    },
    initSound() {
      // Skip if already fully initialized
      if (this.soundInitialized) return;
      if (this.audioContext) {
        // AudioContext exists but sound not decoded yet — try decoding preloaded bytes
        this.decodeSoundBuffer();
        return;
      }

      try {
        // Create audio context - use standard or webkit prefix for broader compatibility
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) {
          logger.console('Web Audio API not supported in this browser');
          return;
        }

        this.audioContext = new AudioContext();

        // Resume audio context if it's suspended (important for iOS)
        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume().catch(err => {
            logger.console('Error resuming audio context:', err);
          });
        }

        // Decode the preloaded sound bytes (if already fetched) or trigger load
        this.decodeSoundBuffer();

        logger.console('Web Audio API initialized successfully');
      } catch (error) {
        logger.console('Error initializing Web Audio API:', error);
      }
    },
    
    preloadSoundBuffer() {
      // Preload the raw audio bytes so they're cached and ready when AudioContext is available
      if (this.soundArrayBuffer || this.soundLoading) return;
      this.soundLoading = true;
      fetch(this.soundUrl)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          return response.arrayBuffer();
        })
        .then(arrayBuffer => {
          this.soundArrayBuffer = arrayBuffer;
          this.soundLoading = false;
          logger.console('Camera sound bytes preloaded');
          // If AudioContext is already available, decode immediately
          this.decodeSoundBuffer();
        })
        .catch(error => {
          logger.console('Error preloading camera sound:', error);
          this.soundLoading = false;
        });
    },
    decodeSoundBuffer() {
      // Decode preloaded bytes into an AudioBuffer (requires active AudioContext)
      if (this.soundBuffer || !this.soundArrayBuffer || !this.audioContext) return;
      // decodeAudioData consumes the buffer, so pass a copy
      const copy = this.soundArrayBuffer.slice(0);
      this.audioContext.decodeAudioData(copy)
        .then(audioBuffer => {
          this.soundBuffer = audioBuffer;
          this.soundInitialized = true;
          logger.console('Camera sound decoded successfully');
        })
        .catch(error => {
          logger.console('Error decoding camera sound:', error);
        });
    },
    loadCameraSound() {
      // Preload bytes if needed, then decode
      if (this.soundBuffer) return;
      if (!this.soundArrayBuffer) {
        this.preloadSoundBuffer();
      } else {
        this.decodeSoundBuffer();
      }
    },
    
    /**
     * Starts (or restarts) the audio for the shutter INSIDE a gesture of the person. iOS only lets an
     * AudioContext start from a `touchend` or a `click` (not from `touchstart`), and only while the
     * gesture is being handled: a resume after an `await` no longer counts. The context used to be
     * resumed on the first `touchstart` and never again inside a gesture, so on iOS it stayed suspended
     * for good and the photo made no sound (Adrián, 30-sep-2026). Chrome was never affected: with the
     * camera open, the page's audio is already unlocked. Browsers also suspend a context on their own
     * (a call, the app in the background), which is why this runs on every tap, not once. On iOS the
     * shutter no longer uses Web Audio at all (playShutterElement). Never throws into the tap.
     */
    unlockAudio() {
      try {
        if (this.isIOS()) {
          // On iOS the shutter plays through its <audio> element. A load() inside a gesture is what
          // lets a later play() through (the same trick Howler uses), and it starts the download:
          // iOS ignores `preload` on an element nobody has touched. Once is enough. No AudioContext is
          // kept running for it: a running one renders silence for the whole camera session.
          if (!this._shutterPrimed) {
            const element = this.shutterElement();
            if (element) { element.load(); this._shutterPrimed = true; }
          }
          return;
        }
        if (!this.audioContext) this.initSound();
        const context = this.audioContext;
        if (context && context.state !== 'running' && context.state !== 'closed') {
          const resumed = context.resume();
          if (resumed && typeof resumed.catch === 'function') resumed.catch(() => {});
        }
      } catch (_) { /* the photo matters, not its sound */ }
    },
    /** The <audio> element the shutter plays on iOS, created (and loaded) once. */
    shutterElement() {
      if (!this._shutterAudio && typeof Audio === 'function') {
        this._shutterAudio = new Audio(this.soundUrl);
        this._shutterAudio.preload = 'auto';
      }
      return this._shutterAudio || null;
    },
    /**
     * On iOS the shutter sounds through an <audio> element, like the correct and error sounds, which
     * are heard there. Web Audio on iOS goes quiet with the silent switch on the side; an audio element
     * does not. If iOS still refuses the element, `onBlocked` tries Web Audio, as a last resort.
     * @returns {boolean} whether it got as far as asking to play (false: use Web Audio now)
     */
    playShutterElement(onBlocked) {
      try {
        const element = this.shutterElement();
        if (!element) return false;
        element.currentTime = 0;
        const played = element.play();
        if (played && typeof played.catch === 'function') {
          played.catch((error) => {
            logger.console('Shutter sound blocked on the audio element, falling back to Web Audio:', error);
            if (typeof onBlocked === 'function') onBlocked();
          });
        }
        return true;
      } catch (_) {
        return false;
      }
    },
    playCameraSound() {
      if (this.isIOS() && this.playShutterElement(() => this.playShutterWebAudio())) return;
      this.playShutterWebAudio();
    },
    playShutterWebAudio() {
      Promise.resolve().then(async () => {
        // Initialize sound if needed
        if (!this.audioContext) {
          this.initSound();
          if (!this.audioContext) return;
        }

        try {
          // Resume context if suspended (mobile browsers often suspend it).
          // Must await: without it, source.start(0) can fire while resume() is still pending,
          // producing no audible output on iOS PWA cold start.
          if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
          }

          // If we have the sound buffer, play it
          if (this.soundBuffer) {
            // Create a buffer source
            const source = this.audioContext.createBufferSource();
            source.buffer = this.soundBuffer;

            // Create a gain node for volume control
            const gainNode = this.audioContext.createGain();
            gainNode.gain.value = 1.0; // Full volume

            // Connect the source to the gain node and then to the destination
            source.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            // Play the sound
            source.start(0);
            logger.console('Playing camera sound');
          } else {
            // If sound is not loaded yet, try to load it
            if (!this.soundLoading) {
              logger.console('Sound not loaded yet, attempting to load');
              this.loadCameraSound();

              // Play a simple beep as fallback
              this.playFallbackSound();
            } else {
              logger.console('Sound is still loading, playing fallback');
              this.playFallbackSound();
            }
          }
        } catch (error) {
          logger.console('Error playing camera sound:', error);
          // Try fallback sound
          this.playFallbackSound();
        }
      });
    },
    
    playFallbackSound() {
      // Play a simple beep as fallback
      try {
        if (!this.audioContext) return;

        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.01);
        gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.1);

        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + 0.1);

        logger.console('Playing fallback sound');
      } catch (error) {
        logger.console('Error playing fallback sound:', error);
      }
    },
    onVisibilityChange() {
      if (document.visibilityState !== 'visible') {
        logger.console('[CAMERA] App went hidden (screen locked or tab hidden)');
        return;
      }

      logger.console('[CAMERA] App became visible again (screen unlocked), checking camera track state');

      // No stream at all — reinitialize
      if (!this.mediaStream || !this.videoTrack) {
        logger.console('[CAMERA] No active stream/track, reinitializing camera');
        this.stopCamera();
        this.initCamera();
        return;
      }

      // The OS kills the camera track on Android when the screen locks.
      // The video element keeps "playing" but delivers no new frames — undetectable via paused/ended.
      // readyState === 'ended' is the reliable signal that the track is dead.
      if (this.videoTrack.readyState === 'ended') {
        logger.console('[CAMERA] Video track ended after screen unlock, reinitializing camera');
        this.stopCamera();
        this.initCamera();
        return;
      }

      // Track reports "live" but may be a zombie (common on realme/Xiaomi/OPPO devices).
      // Check if the video element is actually receiving frames by inspecting currentTime
      // and scheduling a frame-delivery check after a short delay.
      if (this.previewVideo?.paused) {
        logger.console('[CAMERA] Video paused after screen unlock, attempting to resume');
        this.previewVideo.play().catch(err => {
          logger.console('[CAMERA] Resume failed, reinitializing camera:', err);
          this.stopCamera();
          this.initCamera();
        });
      }

      // Delayed zombie-track detection: if the video is "playing" but stuck on the
      // same frame, the track is effectively dead even though readyState === 'live'.
      const timeBefore = this.previewVideo?.currentTime;
      setTimeout(() => {
        if (!this.cameraReady || !this.previewVideo) return;
        const timeAfter = this.previewVideo.currentTime;
        if (timeAfter === timeBefore && this.videoTrack?.readyState === 'live') {
          logger.console(`[CAMERA] Zombie track detected: currentTime stuck at ${timeBefore}s, reinitializing camera`);
          this.stopCamera();
          this.initCamera();
        }
      }, 1500);
    },

    async applyExposureCompensation(capabilities) {
      if (!this.videoTrack || !capabilities?.exposureCompensation) return;
      const exposureComp = this.settings.cameraExposureComp;
      if (exposureComp == null || exposureComp === 0) return;

      const clamped = Math.max(capabilities.exposureCompensation.min,
        Math.min(capabilities.exposureCompensation.max, exposureComp));
      try {
        await this.videoTrack.applyConstraints({ advanced: [{ exposureCompensation: clamped }] });
        logger.console(`Step 4b: Applied exposure compensation ${clamped}`);
      } catch (err) {
        logger.console('Step 4b: Exposure compensation not supported:', err);
      }
    },
    emitStreamInfo() {
      if (!this.videoTrack) return;
      const trackSettings = this.videoTrack.getSettings();
      const capabilities = this.videoTrack.getCapabilities?.();
      const w = trackSettings.width || 0;
      const h = trackSettings.height || 0;

      const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
      const d = gcd(w, h);
      const arStr = d ? `${w / d}:${h / d}` : 'N/A';

      const info = {
        width: w,
        height: h,
        aspectRatio: arStr,
        frameRate: trackSettings.frameRate ? Math.round(trackSettings.frameRate) : 'N/A',
        facingMode: trackSettings.facingMode || 'N/A',
        resizeMode: trackSettings.resizeMode || 'N/A',
        capabilities: capabilities ? JSON.stringify(capabilities, null, 2) : null
      };
      this.$emit('stream-info', info);
    },
    stopCamera() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
      }
      // Detach srcObject to release the hardware camera resource
      if (this.previewVideo) {
        this.previewVideo.srcObject = null;
      }
      this.mediaStream = null;
      this.videoTrack = null;
      this.imageCapture = null;
      this.isCapturing = false;
      this.captureCooldown = false;
      this.cameraReady = false;
      this.cameraMessage = 'Cámara detenida';
      this.showInitButton = true;
      this.stopPlaybackMonitoring();
    },
    startPlaybackMonitoring() {
      // Clear any existing interval
      this.stopPlaybackMonitoring();
      
      // Start a new monitoring interval
      logger.console('Starting video playback monitoring');
      this.playbackMonitorInterval = setInterval(() => {
        if (!this.previewVideo || !this.cameraReady) return;

        // Check if video is paused or ended (don't check currentTime — it's unreliable for live streams)
        if (this.previewVideo.paused || this.previewVideo.ended) {
          logger.console('Video playback detected as paused or ended, attempting to restart');
          this.restartVideoPlayback();
        }

        // Check if video track is disabled or ended
        if (this.videoTrack && (!this.videoTrack.enabled || this.videoTrack.readyState === 'ended')) {
          logger.console('Video track detected as disabled/ended, re-enabling');
          this.videoTrack.enabled = true;
        }
      }, 3000); // Check every 3 seconds (less frequent — restarts are rare)
    },
    
    stopPlaybackMonitoring() {
      if (this.playbackMonitorInterval) {
        clearInterval(this.playbackMonitorInterval);
        this.playbackMonitorInterval = null;
      }
    },
    
    restartVideoPlayback() {
      if (!this.previewVideo || !this.mediaStream) return;
      
      logger.console('Attempting to restart video playback');
      
      // Try to play the video
      this.previewVideo.play().catch(error => {
        logger.console('Error restarting video playback:', error);
        
        // If play fails, try resetting the srcObject
        setTimeout(() => {
          const tempStream = this.previewVideo.srcObject;
          this.previewVideo.srcObject = null;
          
          // Force a repaint
          this.previewVideo.style.display = 'none';
          setTimeout(() => {
            this.previewVideo.style.display = 'block';
            this.previewVideo.srcObject = tempStream;
            
            // Try to play again
            this.previewVideo.play().catch(secondError => {
              logger.console('Second attempt to restart video playback failed:', secondError);
              
              // If still failing, try toggling video tracks
              if (this.videoTrack) {
                const enabled = this.videoTrack.enabled;
                this.videoTrack.enabled = false;
                setTimeout(() => {
                  this.videoTrack.enabled = enabled;
                  this.previewVideo.play().catch(e => {
                    logger.error('Final attempt to restart video playback failed:', {error: e});
                  });
                }, 50);
              }
            });
          }, 50);
        }, 50);
      });
    },
  }
};
</script>

<style scoped>
.camera-preview {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: var(--bz-p-0c1117);
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-video {
  border-radius: 16px;
  display: block;
  /* Prevent zooming/cropping while maintaining aspect ratio */
  object-fit: contain;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  margin: 0 auto;
  /* iOS PWA fixes - maintain hardware acceleration */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  -webkit-perspective: 1000;
  perspective: 1000;
  /* Prevent controls */
  pointer-events: none;
  -webkit-user-select: none;
  user-select: none;
}

/* Additional global styles to hide video controls */
video::-webkit-media-controls,
video::-webkit-media-controls-enclosure,
video::-webkit-media-controls-panel,
video::-webkit-media-controls-play-button,
video::-webkit-media-controls-timeline,
video::-webkit-media-controls-current-time-display,
video::-webkit-media-controls-time-remaining-display,
video::-webkit-media-controls-time-control,
video::-webkit-media-controls-toggle-closed-captions-button,
video::-webkit-media-controls-volume-slider,
video::-webkit-media-controls-mute-button,
video::-webkit-media-controls-fullscreen-button,
video::-webkit-media-controls-rewind-button,
video::-webkit-media-controls-return-to-realtime-button,
video::-webkit-media-controls-seek-back-button,
video::-webkit-media-controls-seek-forward-button,
video::-webkit-media-controls-timeline-container,
video::-webkit-media-controls-status-display,
video::-webkit-media-controls-closed-captions-container,
video::-webkit-media-controls-closed-captions-track-list,
video::-webkit-media-controls-volume-control-container,
video::-webkit-media-controls-volume-control-hover-background,
video::-webkit-media-controls-volume-control-slider-container,
video::-webkit-media-controls-volume-control-slider,
video::-webkit-media-controls-fullscreen-volume-control-slider,
video::-webkit-media-controls-fullscreen-volume-slider,
video::-webkit-media-controls-fullscreen-volume-min-button,
video::-webkit-media-controls-fullscreen-volume-max-button,
video::-webkit-media-controls-fullscreen-volume-slider-container {
  display: none !important;
  opacity: 0 !important;
  pointer-events: none !important;
  width: 0px !important;
  height: 0px !important;
  position: absolute !important;
  visibility: hidden !important;
}

/* Same palette and rhythm as the login boot panel (LoginScreen.vue), which is the app's other
   full-area "something needs a person" state — one composition the operator learns once. */
.camera-message {
  --ink: var(--bz-ink--slate-50);
  --ink-2: var(--bz-p-slate-300);
  --muted: var(--bz-p-slate-400);
  --code: var(--bz-p-slate-500);
  --accent: var(--bz-p-blue-400);
  --accent-hover: var(--bz-p-blue-500);
  --on-accent: var(--bz-p-0c1117);
  --warn: var(--bz-p-amber-400);
  --danger: var(--bz-p-red-400);

  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-family: "DM Sans", sans-serif;
  /* Wider than the old 300px: the instructions are full sentences, and three-word lines are what
     made the previous panel unreadable across a kitchen. */
  width: min(clamp(320px, 80vw, 420px), calc(100% - 32px));
}

/* The entrance rises 6 px: only for those who have not asked the system for less motion. */
@media (prefers-reduced-motion: no-preference) {
  .camera-message {
    animation: cameraFaultIn 0.2s cubic-bezier(0.22, 1, 0.36, 1);
  }
}

@media (max-width: 767px) {
  .camera-message {
    top: 42%;
  }
}

@keyframes cameraFaultIn {
  from { opacity: 0; transform: translate(-50%, calc(-50% + 6px)); }
  to   { opacity: 1; transform: translate(-50%, -50%); }
}

/* Spinning = we are still trying. A static icon = we stopped and are waiting for a person. The
   distinction is the whole point of the panel, so the two must never look alike. */
.camera-spinner {
  font-size: 2.25rem;
  line-height: 1;
  color: var(--accent);
  margin-bottom: 1.25rem;
}

.camera-fault-icon {
  font-size: 2.25rem;
  line-height: 1;
  margin-bottom: 1.25rem;
}
.camera-fault-icon.tone-warn { color: var(--warn); }
.camera-fault-icon.tone-danger { color: var(--danger); }

.camera-fault-title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.3;
  text-wrap: balance;
}

.camera-fault-body {
  margin: 0.75rem 0 0;
  font-size: 1.0625rem;
  color: var(--ink-2);
  line-height: 1.45;
  text-wrap: pretty;
}

.camera-error-code {
  margin: 1.25rem 0 0;
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  color: var(--code);
  user-select: text;
}

.init-camera-button {
  margin-top: 1.5rem;
  padding: 14px 32px;
  /* 48px minimum touch target: this button is pressed with a thumb, often in a hurry. */
  min-height: 48px;
  font-size: 1.0625rem;
  font-weight: 600;
  font-family: "DM Sans", sans-serif;
  background-color: var(--accent);
  color: var(--on-accent);
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.init-camera-button:active {
  background-color: var(--accent-hover);
}

.flash {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--bz-p-white-a50);
  opacity: 0;
  pointer-events: none;
  z-index: 999;
  will-change: opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
  transition: opacity 0.05s ease-out;
}

.flash.active {
  opacity: 0.7;
}

/* Capture-in-progress pulsing border (pseudo-element renders above video) */
.camera-preview {
  /* The flash (z-index 999) lives inside this element. In immersive mode a z-index here already
     confines it; without one — mobile, RBE — it painted over the buttons instead of over the
     video. `isolation` gives it the same stacking context everywhere, with no paint cost. */
  isolation: isolate;
}

.camera-preview::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 16px;
  pointer-events: none;
  z-index: 900;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.camera-preview.is-capturing::after {
  opacity: 1;
  transition: none; /* a state indicator, not an animation: it goes on and off */
  /* Static bright border. This used to be an infinite `capture-pulse` animation on box-shadow,
     but box-shadow is NOT GPU-compositable: animating it forced a style recalc + full repaint on
     the main thread every frame (~20fps) for the whole capture, and the toBlob callback queued
     behind those repaints. Measured on a Redmi Pad Pro, that alone made each capture ~5.7x slower
     (tapToBlob 1852ms -> 323ms). A static shadow paints once; the `::after` base rule already fades
     it in via `transition: opacity` (compositable). RULE: never animate non-compositable properties
     (box-shadow / filter / width / top) on capture-active state — only opacity / transform. */
  /* One flat inset border. The three blurred layers underneath it (up to 180px of blur and 90px
     of spread, over a full-screen surface) had to be rasterised on the GPU every time the frame
     appeared and again when its layer was dropped — on an Adreno 610, sharing the GPU with a live
     video and six backdrop-filters, that is margin spent on a glow nobody asked for. It also
     never faded: the transition only lists `opacity`, so the shadow always snapped anyway.
     Flat colour is the house style regardless. */
  box-shadow: inset 0 0 0 4px var(--bz-p-white-a90);
}

/* "Capturando..." centered overlay */
.capturing-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--bz-p-black-a45);
  border-radius: 24px;
  padding: 12px 28px;
  color: var(--bz-p-white);
  font-family: "DM Sans", sans-serif;
  font-size: 20px;
  font-weight: 600;
  pointer-events: none;
  z-index: 10;
  white-space: nowrap;
}

.capture-fade-enter-active {
  transition: opacity 0.15s ease;
}

.capture-fade-leave-active {
  transition: opacity 0.3s ease;
}

.capture-fade-enter-from,
.capture-fade-leave-to {
  opacity: 0;
}
/* ════ On the design system (src/ui/lane.js: `ui-system-lane`, per restaurant) ════
   The camera's own states in the system's words: the inks and the action's blue by their tokens, the
   title at 22/600, the body at 16, a main button of 56; «capturing» on a solid capsule, never glass. */
:root[data-bz-lane="system"] .camera-message {
  --ink: var(--bz-ink);
  --ink-2: var(--bz-ink-2);
  --code: var(--bz-ink-2);
  --accent: var(--bz-action-ink);
  --warn: var(--bz-warn);
  --danger: var(--bz-danger);
  font-family: var(--bz-font);
}

:root[data-bz-lane="system"] .camera-fault-icon {
  margin-bottom: var(--bz-space-5);
  font-size: var(--bz-icon-lg);
}

:root[data-bz-lane="system"] .camera-fault-title {
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .camera-fault-body {
  margin-top: var(--bz-space-3);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
}

:root[data-bz-lane="system"] .camera-error-code {
  font-size: var(--bz-text-xs);
  line-height: var(--bz-leading-xs);
}

:root[data-bz-lane="system"] .init-camera-button,
:root[data-bz-lane="system"] .init-camera-button:active {
  min-height: var(--bz-target-md);
  margin-top: var(--bz-space-6);
  padding: 0 var(--bz-space-7);
  border-radius: var(--bz-radius-md);
  background: var(--bz-action);
  color: var(--bz-on-action);
  font-family: var(--bz-font);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .capturing-overlay {
  padding: var(--bz-space-3) var(--bz-space-6);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-full);
  background: var(--bz-raised);
  color: var(--bz-ink);
  font-family: var(--bz-font);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  font-weight: var(--bz-weight-label);
}
</style>